import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Section, Heading, Spinner } from "../components/Bits";
import { PlantBadge } from "../components/Bits";
import ImageUpload from "../components/ImageUpload";
import EmailTemplatesAdmin from "../components/EmailTemplatesAdmin";
import AIPlantGenerator from "../components/AIPlantGenerator";
import SocialCardsAdmin from "../components/admin/SocialAdmin";
import ArticlesAdmin from "../components/admin/ArticlesAdmin";
import PagesAdmin from "../components/admin/PagesAdmin";
import BlocksAdmin from "../components/admin/BlocksAdmin";
import MessagesAdmin from "../components/admin/MessagesAdmin";
import AdminAccountsPanel from "../components/admin/AdminAccountsPanel";
import WeeklyPlantsPanel from "../components/admin/WeeklyPlantsPanel";
import TourScriptsAdmin from "../components/admin/TourScriptsAdmin";
import DownloadsAdmin from "../components/admin/DownloadsAdmin";
import BroadcastsAdmin from "../components/admin/BroadcastsAdmin";
import BulkPhotoFill from "../components/admin/BulkPhotoFill";
import PlantEnrichPanel from "../components/admin/PlantEnrichPanel";
import PlantPdfButton from "../components/admin/PlantPdfButton";
import ProfileDataButton from "../components/admin/ProfileDataButton";
import StockPhotoPicker from "../components/admin/StockPhotoPicker";
import AdminLogin from "../components/admin/AdminLogin";
import { TourTextsSettings, DontDoSettings, CalendarPhotosSettings, ContactTextsSettings } from "../components/admin/SiteTextsSettings";
import PlantDuplicatesPanel from "../components/admin/PlantDuplicatesPanel";
import { createPortal } from "react-dom";
import { Lock, Trash2, Pencil, Plus, LogOut, RefreshCw, Send, Star, Upload, Download, FileSpreadsheet, Eye, EyeOff, Loader2 } from "lucide-react";

const TABS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "plants", label: "Pflanzen" },
  { key: "articles", label: "Magazin" },
  { key: "weekly", label: "Pflanze d. Woche" },
  { key: "scripts", label: "Tour-Skripte" },
  { key: "downloads", label: "Downloads" },
  { key: "pages", label: "Seiten" },
  { key: "blocks", label: "Block-Editor" },
  { key: "messages", label: "Banner & Popups" },
  { key: "social", label: "Social Cards" },
  { key: "tours", label: "Touren" },
  { key: "users", label: "User" },
  { key: "bookings", label: "Buchungen" },
  { key: "waitlist", label: "Warteliste" },
  { key: "contacts", label: "Nachrichten" },
  { key: "community", label: "Community" },
  { key: "newsletter", label: "Newsletter" },
  { key: "broadcast", label: "Broadcast" },
  { key: "analytics", label: "Analytics" },
  { key: "emails", label: "E-Mail-Templates" },
  { key: "admins", label: "Admin-Konten" },
  { key: "settings", label: "Einstellungen" },
];

export default function Admin() {
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => m.remove();
  }, []);
  // null = checking, true/false = session state (httpOnly cookie, auto-logout after 12 h)
  const [authed, setAuthed] = useState(null);
  const [tab, setTab] = useState("dashboard");

  useEffect(() => {
    localStorage.removeItem("wp_admin_pin");
    localStorage.removeItem("wp_admin_token");
    api.get("/admin/auth/me").then(() => setAuthed(true)).catch(() => setAuthed(false));
    const id = api.interceptors.response.use((r) => r, (err) => {
      const url = err?.config?.url || "";
      if (err?.response?.status === 401 && url.includes("/admin") && !url.includes("/admin/auth/")) setAuthed(false);
      return Promise.reject(err);
    });
    return () => api.interceptors.response.eject(id);
  }, []);

  const logout = async () => {
    await api.post("/admin/auth/logout").catch(() => {});
    setAuthed(false);
  };

  if (authed === null) return <Section><Spinner /></Section>;
  if (!authed) return <AdminLogin onLoggedIn={() => setAuthed(true)} />;

  return (
    <Section>
      <div className="flex items-center justify-between mb-5 sm:mb-6 flex-wrap gap-3">
        <Heading overline="Backoffice" title="Admin-Bereich" />
        <button onClick={logout} className="btn-secondary text-sm" data-testid="admin-logout"><LogOut className="w-4 h-4" /> Abmelden</button>
      </div>

      {/* Mobile: dropdown selector */}
      <div className="sm:hidden mb-5">
        <label className="block">
          <div className="overline mb-1.5">Bereich</div>
          <select
            value={tab}
            onChange={(e) => setTab(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border text-base font-medium bg-white"
            style={{ borderColor: "var(--wp-border)", color: "var(--wp-olive)" }}
            data-testid="admin-tab-mobile-select"
          >
            {TABS.map((t) => (
              <option key={t.key} value={t.key}>{t.label}</option>
            ))}
          </select>
        </label>
      </div>

      {/* Desktop: pill tabs */}
      <div className="hidden sm:flex flex-wrap gap-2 mb-8">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors border ${
              tab === t.key ? "bg-[var(--wp-primary)] text-[var(--wp-cream)] border-[var(--wp-primary)]" : "bg-white hover:bg-[var(--wp-cream)]"
            }`}
            style={tab !== t.key ? { borderColor: "var(--wp-border)" } : {}}
            data-testid={`admin-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "dashboard" && <DashboardAdmin onNavigate={setTab} />}
      {tab === "plants" && <PlantsAdmin />}
      {tab === "articles" && <ArticlesAdmin />}
      {tab === "weekly" && <WeeklyPlantsPanel />}
      {tab === "scripts" && <TourScriptsAdmin />}
      {tab === "downloads" && <DownloadsAdmin />}
      {tab === "pages" && <PagesAdmin />}
      {tab === "blocks" && <BlocksAdmin />}
      {tab === "messages" && <MessagesAdmin />}
      {tab === "social" && <SocialCardsAdmin />}
      {tab === "tours" && <ToursAdmin />}
      {tab === "users" && <UsersAdmin />}
      {tab === "bookings" && <BookingsAdmin />}
      {tab === "waitlist" && <WaitlistAdmin />}
      {tab === "contacts" && <ContactsAdmin />}
      {tab === "community" && <CommunityAdmin />}
      {tab === "newsletter" && <NewsletterAdmin />}
      {tab === "broadcast" && <BroadcastsAdmin />}
      {tab === "analytics" && <AnalyticsAdmin />}
      {tab === "emails" && <EmailTemplatesAdmin />}
      {tab === "admins" && <AdminAccountsPanel goToUsers={() => setTab("users")} />}
      {tab === "settings" && <SettingsAdmin />}
    </Section>
  );
}

// Shared portal wrapper – ensures modal escapes any transformed/stacking ancestor.
function AdminModalBackdrop({ onClose, children, testid }) {
  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ backgroundColor: "rgba(26,47,28,0.6)" }}
      onClick={onClose}
      data-testid={testid}
    >
      {children}
    </div>,
    document.body,
  );
}

function PlantsAdmin() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [sortBy, setSortBy] = useState("name_de");

  const load = ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    api.get("/admin/plants").then((r) => setList(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const del = (id) => {
    if (!window.confirm("Pflanze wirklich löschen?")) return;
    api.delete(`/admin/plants/${id}`).then(() => {
      toast.success("Gelöscht");
      load();
    });
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...edit,
      season: typeof edit.season === "string" ? edit.season.split(",").map((s) => parseInt(s.trim())).filter(Boolean) : edit.season,
      season_possible: typeof edit.season_possible === "string" ? edit.season_possible.split(",").map((s) => parseInt(s.trim())).filter(Boolean) : (edit.season_possible || []),
      tags: typeof edit.tags === "string" ? edit.tags.split(",").map((s) => s.trim()).filter(Boolean) : edit.tags,
      images: Array.isArray(edit.images) ? edit.images.filter(Boolean) : [],
      recipes: edit.recipes || [],
    };
    delete payload.id; delete payload.created_at; delete payload.updated_at;
    try {
      if (edit.id) await api.put(`/admin/plants/${edit.id}`, payload);
      else await api.post("/admin/plants", payload);
      toast.success("Gespeichert");
      setEdit(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  const rejectPhoto = async (url) => {
    try {
      const { data } = await api.post(`/admin/plants/${edit.id}/reject-photo`, { url });
      setEdit((prev) => {
        const next = { ...prev };
        if ("images" in data) {
          const kept = (prev.images || []).filter((u) => u !== url);
          next.images = [...kept, ...data.images.filter((u) => !kept.some((k) => k === u || k.endsWith(u)))];
        }
        if ("image_url" in data) { next.image_url = data.image_url; next.image_credit = data.image_credit; }
        return next;
      });
      toast.success(data.replaced ? "Foto ersetzt durch ein besseres von Wikimedia" : "Foto entfernt – kein passendes Ersatzfoto gefunden");
      load({ silent: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Ersetzen");
    }
  };


  const filtered = (() => {
    const q = search.trim().toLowerCase();
    let out = list.filter((p) => {
      if (category !== "all" && (p.category || "edible") !== category) return false;
      if (q) {
        const hay = `${p.name_de || ""} ${p.name_lat || ""} ${p.name_lu || ""} ${(p.tags || []).join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const cmp = (a, b, key) => (a[key] || "").toString().localeCompare((b[key] || "").toString(), "de");
    switch (sortBy) {
      case "name_de_desc": out.sort((a, b) => cmp(b, a, "name_de")); break;
      case "name_lat": out.sort((a, b) => cmp(a, b, "name_lat")); break;
      case "category": out.sort((a, b) => cmp(a, b, "category") || cmp(a, b, "name_de")); break;
      case "updated_at": out.sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || "")); break;
      case "created_at": out.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")); break;
      default: out.sort((a, b) => cmp(a, b, "name_de")); // name_de A-Z
    }
    out.sort((a, b) => Number(!!a.archived) - Number(!!b.archived));
    return out;
  })();

  const toggleArchive = async (p) => {
    const next = !p.archived;
    if (next && !window.confirm(`„${p.name_de}“ auf der Website ausblenden? Der Eintrag bleibt im Backoffice erhalten.`)) return;
    await api.post(`/admin/plants/${p.id}/archive`, { archived: next });
    toast.success(next ? "Pflanze ausgeblendet" : "Pflanze wieder sichtbar");
    load({ silent: true });
  };

  const archivedBadge = (p, suffix = "") => p.archived && (
    <span className="ml-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full align-middle" style={{ backgroundColor: "rgba(167,58,58,0.12)", color: "var(--wp-toxic)" }} data-testid={`plant-archived-${p.slug}${suffix}`}>
      deaktiviert{p.merged_into ? ` → ${p.merged_into}` : ""}
    </span>
  );

  const archiveBtn = (p, suffix = "") => (
    <button onClick={() => toggleArchive(p)} className="px-2" title={p.archived ? "Wieder auf der Website zeigen" : "Auf der Website ausblenden"} data-testid={`archive-plant-${p.slug}${suffix}`}>
      {p.archived ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
    </button>
  );

  if (loading) return <Spinner />;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="text-sm opacity-70" data-testid="plants-count">{filtered.length} von {list.length} Pflanzen</div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={load} className="btn-secondary !py-2 !px-3" title="Aktualisieren"><RefreshCw className="w-4 h-4" /></button>
          <CSVControls onImportDone={() => load({ silent: true })} />
          <AIPlantGenerator onDone={() => load({ silent: true })} />
          <BulkAIFillButton onDone={() => load({ silent: true })} />
          <BulkPhotoFill onDone={() => load({ silent: true })} />
          <button onClick={() => setEdit({ slug: "", name_de: "", name_lat: "", category: "edible", season: [], tags: [], image_url: "", images: [], short_description: "", description: "" })} className="btn-primary !py-2 !px-4" data-testid="admin-new-plant">
            <Plus className="w-4 h-4" /> Neu
          </button>
        </div>
      </div>
      <div className="mb-4"><PlantEnrichPanel onDone={() => load({ silent: true })} /></div>

      <PlantDuplicatesPanel reloadKey={list.length} onChanged={() => load({ silent: true })} />

      {/* Filter / Search / Sort bar */}
      <div className="plant-card p-3 mb-3 flex flex-wrap items-center gap-2" data-testid="plants-filterbar">
        <div className="relative flex-1 min-w-[180px]">
          <input
            type="search"
            placeholder="Suchen (Name, Latein, Tags …)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-full border text-sm"
            style={{ borderColor: "var(--wp-border)", backgroundColor: "#fff" }}
            data-testid="plants-search"
          />
          <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-xs opacity-50" />
        </div>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="px-3 py-2 rounded-full border text-sm"
          style={{ borderColor: "var(--wp-border)", backgroundColor: "#fff" }}
          data-testid="plants-category-filter"
        >
          <option value="all">Alle Kategorien</option>
          <option value="edible">Essbar</option>
          <option value="medicinal">Heilpflanze</option>
          <option value="toxic">Giftig</option>
          <option value="tree">Baum</option>
          <option value="shrub">Strauch</option>
          <option value="invasive">Invasiv</option>
        </select>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-3 py-2 rounded-full border text-sm"
          style={{ borderColor: "var(--wp-border)", backgroundColor: "#fff" }}
          data-testid="plants-sort"
        >
          <option value="name_de">Name A–Z</option>
          <option value="name_de_desc">Name Z–A</option>
          <option value="name_lat">Lateinisch A–Z</option>
          <option value="category">Nach Kategorie</option>
          <option value="updated_at">Zuletzt geändert</option>
          <option value="created_at">Zuletzt hinzugefügt</option>
        </select>
        {(search || category !== "all" || sortBy !== "name_de") && (
          <button
            type="button"
            onClick={() => { setSearch(""); setCategory("all"); setSortBy("name_de"); }}
            className="text-xs underline opacity-70 hover:opacity-100 px-2"
            data-testid="plants-filter-reset"
          >
            Zurücksetzen
          </button>
        )}
      </div>

      {filtered.length === 0 && (
        <div className="plant-card p-8 text-center text-sm opacity-70" data-testid="plants-empty">
          Keine Pflanzen passen zu deinem Filter.
        </div>
      )}

      {/* Desktop: classic table */}
      <div className={`plant-card overflow-x-auto hidden md:block ${filtered.length === 0 ? "hidden" : ""}`}>
        <table className="w-full text-sm">
          <thead className="text-left bg-[var(--wp-cream)]">
            <tr>
              <th className="p-3">Name</th><th className="p-3">Latein</th><th className="p-3">Kategorie</th><th className="p-3 text-right whitespace-nowrap">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} className={`border-t ${p.archived ? "opacity-60" : ""}`} style={{ borderColor: "var(--wp-border)" }}>
                <td className="p-3 font-medium">{p.name_de}{archivedBadge(p)}</td>
                <td className="p-3 italic opacity-70">{p.name_lat}</td>
                <td className="p-3">{p.category}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <ProfileDataButton plant={p} />
                  <PlantPdfButton plant={p} />
                  <button onClick={async () => { await api.post(`/admin/plants/${p.id}/set-potw`); toast.success("Als Pflanze der Woche gesetzt"); }} className="px-2" title="Als Pflanze der Woche" data-testid={`potw-plant-${p.slug}`}><Star className="w-4 h-4" /></button>
                  <button onClick={() => setEdit(p)} className="px-2" data-testid={`edit-plant-${p.slug}`}><Pencil className="w-4 h-4" /></button>
                  {archiveBtn(p)}
                  <button onClick={() => del(p.id)} className="px-2 text-[var(--wp-toxic)]" data-testid={`delete-plant-${p.slug}`}><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: card list with clearly tappable buttons */}
      <div className="md:hidden space-y-3" data-testid="plants-mobile-list">
        {filtered.map((p) => (
          <div key={p.id} className={`plant-card p-4 ${p.archived ? "opacity-60" : ""}`} data-testid={`plant-card-${p.slug}`}>
            <div className="flex items-start gap-3 mb-3">
              {p.image_url ? <img src={p.image_url} alt={p.name_de} className="w-14 h-14 rounded-lg object-cover shrink-0" /> : <div className="w-14 h-14 rounded-lg bg-[var(--wp-cream)] shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-base truncate">{p.name_de}{archivedBadge(p, "-mobile")}</div>
                <div className="italic text-xs opacity-70 truncate">{p.name_lat}</div>
                <div className="text-[11px] opacity-60 mt-0.5">{p.category}</div>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setEdit(p)}
                className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-full text-sm font-medium border"
                style={{ borderColor: "var(--wp-border)", backgroundColor: "var(--wp-cream)" }}
                data-testid={`mobile-edit-${p.slug}`}
              >
                <Pencil className="w-4 h-4" /> Bearbeiten
              </button>
              <PlantPdfButton plant={p} variant="text" />
              <ProfileDataButton plant={p} variant="text" />
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-full border shrink-0" style={{ borderColor: "var(--wp-border)" }}>{archiveBtn(p, "-mobile")}</span>
              <button
                onClick={async () => { await api.post(`/admin/plants/${p.id}/set-potw`); toast.success("Als Pflanze der Woche gesetzt"); }}
                className="inline-flex items-center justify-center w-11 h-11 rounded-full border shrink-0"
                style={{ borderColor: "var(--wp-border)" }}
                title="Als Pflanze der Woche"
                data-testid={`mobile-potw-${p.slug}`}
              >
                <Star className="w-4 h-4" />
              </button>
              <button
                onClick={() => del(p.id)}
                className="inline-flex items-center justify-center w-11 h-11 rounded-full border shrink-0"
                style={{ borderColor: "var(--wp-toxic)", color: "var(--wp-toxic)" }}
                title="Löschen"
                data-testid={`mobile-delete-${p.slug}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {edit && (
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ backgroundColor: "rgba(26,47,28,0.6)" }} onClick={() => setEdit(null)}>
          <div className="bg-white rounded-t-3xl sm:rounded-2xl max-w-2xl w-full p-5 sm:p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 mb-4">
              <h3 className="font-display text-2xl">{edit.id ? "Bearbeiten" : "Neue Pflanze"}</h3>
              {edit.id && (
                <AIPlantFillButton plant={edit} onFilled={(updated) => setEdit({ ...edit, ...updated })} />
              )}
            </div>
            <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <input required placeholder="Slug" value={edit.slug || ""} onChange={(e) => setEdit({ ...edit, slug: e.target.value })} className="px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} />
              <select value={edit.category || "edible"} onChange={(e) => setEdit({ ...edit, category: e.target.value })} className="px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }}>
                <option value="edible">essbar</option>
                <option value="medicinal">heilkraut</option>
                <option value="toxic">giftig</option>
                <option value="tree">Baum</option>
                <option value="shrub">Strauch</option>
              </select>
              <input required placeholder="Name DE" value={edit.name_de || ""} onChange={(e) => setEdit({ ...edit, name_de: e.target.value })} className="px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} />
              <input required placeholder="Lateinisch" value={edit.name_lat || ""} onChange={(e) => setEdit({ ...edit, name_lat: e.target.value })} className="px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} />
              <input placeholder="Saison – Beste Zeit (1,2,3...)" title="Monate 1-12, kommagetrennt. Wird im Kalender dunkelgrün angezeigt." value={Array.isArray(edit.season) ? edit.season.join(",") : edit.season || ""} onChange={(e) => setEdit({ ...edit, season: e.target.value })} className="px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} data-testid="plant-field-season" />
              <input placeholder="Saison möglich (2,11 – hellgrün)" title="Zusätzliche Monate, in denen die Pflanze zwar da ist, aber nicht ideal. Wird im Kalender hellgrün angezeigt." value={Array.isArray(edit.season_possible) ? edit.season_possible.join(",") : edit.season_possible || ""} onChange={(e) => setEdit({ ...edit, season_possible: e.target.value })} className="px-3 py-2 rounded-lg border col-span-2" style={{ borderColor: "var(--wp-border)" }} data-testid="plant-field-season-possible" />
              <div className="col-span-2">
                <div className="overline mb-2">Hauptbild</div>
                <div className="flex items-center gap-3">
                  <ImageUpload value={edit.image_url} onChange={(v) => setEdit({ ...edit, image_url: v, image_credit: null })} endpoint="/admin/uploads" testId="plant-image-upload" stock={false} />
                  <input placeholder="oder Bild-URL einfügen" value={edit.image_url || ""} onChange={(e) => setEdit({ ...edit, image_url: e.target.value, image_credit: null })} className="flex-1 px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} />
                </div>
                <AIPlantImageButton plant={edit} onGenerated={(url) => setEdit({ ...edit, image_url: url, image_credit: null })} label="KI-Bild (nur als letzte Option)" />
                <StockPhotoPicker plant={edit} onPicked={(url, credit) => { setEdit({ ...edit, image_url: url, image_credit: credit }); load({ silent: true }); }} />
                {edit.image_credit?.author && (
                  <div className="text-[11px] opacity-60 mt-1" data-testid="plant-image-credit-admin">Foto: {edit.image_credit.author} · {edit.image_credit.license}</div>
                )}
                {edit.id && edit.image_url && (
                  <div className="w-40"><RejectPhotoButton onReject={() => rejectPhoto(edit.image_url)} testId="main-photo-reject" /></div>
                )}
              </div>
              <div className="col-span-2">
                <div className="overline mb-2">Galerie (weitere Bilder)</div>
                <PlantGallery images={edit.images || []} onChange={(imgs) => setEdit({ ...edit, images: imgs })} plant={edit} onReject={edit.id ? rejectPhoto : null} />
              </div>
              <div className="col-span-2">
                <div className="overline mb-2">Verwechslungs-Pflanzen (klickbar)</div>
                <LookAlikePicker
                  selected={edit.look_alike_slugs || []}
                  onChange={(slugs) => setEdit({ ...edit, look_alike_slugs: slugs })}
                  excludeSlug={edit.slug}
                />
              </div>
              <input placeholder="Tags (kommagetrennt)" value={Array.isArray(edit.tags) ? edit.tags.join(",") : edit.tags || ""} onChange={(e) => setEdit({ ...edit, tags: e.target.value })} className="col-span-2 px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} />
              <div className="col-span-2 flex flex-wrap items-center gap-4 text-sm p-3 rounded-lg" style={{ backgroundColor: "var(--wp-cream)" }} data-testid="plant-newsletter-flags">
                <label className="flex items-center gap-2">
                  Artikel
                  <select value={edit.article || ""} onChange={(e) => setEdit({ ...edit, article: e.target.value })} className="px-2 py-1 rounded-lg border bg-white" style={{ borderColor: "var(--wp-border)" }} data-testid="plant-article-select">
                    <option value="">—</option>
                    <option value="der">der</option>
                    <option value="die">die</option>
                    <option value="das">das</option>
                  </select>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!edit.is_native_wild} onChange={(e) => setEdit({ ...edit, is_native_wild: e.target.checked })} data-testid="plant-native-wild-toggle" />
                  Heimische Wildpflanze
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!edit.newsletter_exclude} onChange={(e) => setEdit({ ...edit, newsletter_exclude: e.target.checked })} data-testid="plant-newsletter-exclude-toggle" />
                  Vom Newsletter ausschließen
                </label>
              </div>
              <textarea placeholder="Kurzbeschreibung" value={edit.short_description || ""} onChange={(e) => setEdit({ ...edit, short_description: e.target.value })} className="col-span-2 px-3 py-2 rounded-lg border min-h-[60px]" style={{ borderColor: "var(--wp-border)" }} />
              <textarea placeholder="Beschreibung" value={edit.description || ""} onChange={(e) => setEdit({ ...edit, description: e.target.value })} className="col-span-2 px-3 py-2 rounded-lg border min-h-[80px]" style={{ borderColor: "var(--wp-border)" }} />
              <textarea placeholder="Inhaltsstoffe" value={edit.ingredients || ""} onChange={(e) => setEdit({ ...edit, ingredients: e.target.value })} className="col-span-2 px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} />
              <textarea placeholder="Standort" value={edit.habitat || ""} onChange={(e) => setEdit({ ...edit, habitat: e.target.value })} className="col-span-2 px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} />
              <textarea placeholder="Verwechslungen" value={edit.look_alikes || ""} onChange={(e) => setEdit({ ...edit, look_alikes: e.target.value })} className="col-span-2 px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} />
              <textarea placeholder="Warnungen" value={edit.warnings || ""} onChange={(e) => setEdit({ ...edit, warnings: e.target.value })} className="col-span-2 px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} />
              <textarea placeholder="Verwendung" value={edit.uses || ""} onChange={(e) => setEdit({ ...edit, uses: e.target.value })} className="col-span-2 px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} />
              <textarea
                placeholder="Historischer Hintergrund (Volksmedizin, Mythologie, kultureller Kontext)"
                value={edit.historical_background || ""}
                onChange={(e) => setEdit({ ...edit, historical_background: e.target.value })}
                className="col-span-2 px-3 py-2 rounded-lg border min-h-[80px]"
                style={{ borderColor: "var(--wp-border)" }}
                data-testid="plant-field-historical-background"
              />
              <div className="col-span-2 flex justify-end gap-2 mt-2">
                <button type="button" onClick={() => setEdit(null)} className="btn-secondary">Abbrechen</button>
                <button className="btn-primary" data-testid="plant-form-submit">Speichern</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const KIND_ORDER = { tour: 0, legacy: 2 };
function sortTours(list) {
  const tours = list.filter((t) => t.kind === "tour").sort((a, b) => (a.order || 0) - (b.order || 0));
  const out = [];
  tours.forEach((t) => {
    out.push(t);
    list.filter((d) => d.kind === "date" && d.parent_id === t.id).sort((a, b) => (a.date || "").localeCompare(b.date || "")).forEach((d) => out.push(d));
  });
  const rest = list.filter((t) => !out.includes(t)).sort((a, b) => (KIND_ORDER[a.kind] ?? 1) - (KIND_ORDER[b.kind] ?? 1));
  return [...out, ...rest];
}
const galleryToText = (g) => (g || []).map((x) => `${x.url} | ${x.alt || ""}`).join("\n");
const parseGallery = (txt) => (txt || "").split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
  const [url, ...alt] = l.split("|");
  return { url: url.trim(), alt: alt.join("|").trim() };
});

function TourKindFields({ edit, setEdit, tours }) {
  const inp = "w-full px-3 py-2 rounded-lg border";
  const bc = { borderColor: "var(--wp-border)" };
  const set = (k) => (e) => setEdit({ ...edit, [k]: e.target.value });
  const active = (
    <label className="col-span-2 flex items-center gap-2 cursor-pointer">
      <input type="checkbox" checked={edit.is_active !== false} onChange={(e) => setEdit({ ...edit, is_active: e.target.checked })} data-testid="tour-form-active" />
      Öffentlich sichtbar {edit.kind === "date" && "(nur mit Treffpunkt möglich)"}
    </label>
  );
  if (edit.kind === "tour") {
    return (
      <>
        <label className="col-span-2 block"><div className="overline mb-1">Titel *</div>
          <input required value={edit.title || ""} onChange={set("title")} className={inp} style={bc} data-testid="tour-form-title" /></label>
        <label className="block"><div className="overline mb-1">URL-Slug</div>
          <input placeholder="automatisch aus Titel" value={edit.slug || ""} onChange={set("slug")} className={inp} style={bc} data-testid="tour-form-slug" /></label>
        <label className="block"><div className="overline mb-1">Saison</div>
          <input placeholder="z. B. Frühling bis Herbst" value={edit.season_label || ""} onChange={set("season_label")} className={inp} style={bc} data-testid="tour-form-season" /></label>
        <label className="col-span-2 block"><div className="overline mb-1">Kurzbeschreibung (Karte)</div>
          <textarea value={edit.short_description || ""} onChange={set("short_description")} className={`${inp} min-h-[70px]`} style={bc} data-testid="tour-form-short" /></label>
        <label className="block"><div className="overline mb-1">Alt-Text Titelbild</div>
          <input value={edit.image_alt || ""} onChange={set("image_alt")} className={inp} style={bc} data-testid="tour-form-image-alt" /></label>
        <label className="block"><div className="overline mb-1">Reihenfolge</div>
          <input type="number" value={edit.order ?? 0} onChange={set("order")} className={inp} style={bc} /></label>
        <label className="col-span-2 block"><div className="overline mb-1">Weitere Fotos (je Zeile: URL | Alt-Text)</div>
          <textarea value={edit._gallery_text ?? galleryToText(edit.gallery)} onChange={(e) => setEdit({ ...edit, _gallery_text: e.target.value })} className={`${inp} min-h-[60px] font-mono text-xs`} style={bc} data-testid="tour-form-gallery" /></label>
        <div className="col-span-2 text-xs opacity-70 p-2 rounded" style={{ backgroundColor: "rgba(90,110,63,0.08)" }}>
          Touren haben keinen Ort und kein Datum. Lege Termine über den Button „+ Termin“ in der Liste an – erst dann ist die Tour buchbar.
        </div>
        {active}
      </>
    );
  }
  return (
    <>
      <label className="col-span-2 block"><div className="overline mb-1">Gehört zur Tour *</div>
        <select required value={edit.parent_id || ""} onChange={(e) => { const p = tours.find((t) => t.id === e.target.value); setEdit({ ...edit, parent_id: e.target.value, title: p ? p.title : edit.title }); }} className={inp} style={bc} data-testid="tour-form-parent">
          <option value="">– Tour wählen –</option>
          {tours.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
        </select></label>
      <label className="block"><div className="overline mb-1">Datum &amp; Uhrzeit *</div>
        <input required type="datetime-local" value={edit.date || ""} onChange={set("date")} className={inp} style={bc} data-testid="tour-form-date" /></label>
      <label className="block"><div className="overline mb-1">Treffpunkt * (öffentlich)</div>
        <input required={edit.is_active !== false} placeholder="z. B. Eingang Biodiversum, Remerschen" value={edit.meeting_point || ""} onChange={set("meeting_point")} className={inp} style={bc} data-testid="tour-form-meeting-point" /></label>
      <label className="col-span-2 block"><div className="overline mb-1">Anfahrt / Hinweise (intern – nur in Bestätigungs- &amp; Erinnerungs-Mail)</div>
        <textarea placeholder="Parken, Toiletten, Kartenlink …" value={edit.directions_notes || ""} onChange={set("directions_notes")} className={`${inp} min-h-[70px]`} style={bc} data-testid="tour-form-directions" /></label>
      {active}
    </>
  );
}

function EventQrButton({ tour }) {
  const [open, setOpen] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL;
  const qrImgUrl = `${backend}/api/events/admin/${tour.id}/qrcode.png?_=${Date.now()}`;
  const posterUrl = `${backend}/api/events/admin/${tour.id}/qr-poster.html`;
  const checkinUrl = `${window.location.origin}/event-checkin/${tour.id}`;

  // Fetch QR via authenticated XHR to display inline (browser <img> can't send headers)
  const [imgSrc, setImgSrc] = useState("");
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api
      .get(`/events/admin/${tour.id}/qrcode.png`, { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        const url = URL.createObjectURL(r.data);
        setImgSrc(url);
      })
      .catch(() => toast.error("QR-Code konnte nicht geladen werden"));
    return () => { cancelled = true; };
  }, [open, tour.id]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(checkinUrl);
      toast.success("Link kopiert");
    } catch {
      toast.error("Konnte nicht kopieren");
    }
  };

  const downloadPng = async () => {
    try {
      const r = await api.get(`/events/admin/${tour.id}/qrcode.png`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `event-qr-${(tour.title || tour.id).replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download fehlgeschlagen");
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
        style={{ backgroundColor: "var(--wp-olive)", color: "var(--wp-cream)" }}
        title="QR-Code für Event-Anmeldung"
        data-testid={`event-qr-btn-${tour.id}`}
      >
        <i className="fa-solid fa-qrcode" /> QR-Code
      </button>
      {open && createPortal(
        <div
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{ backgroundColor: "rgba(26,47,28,0.7)" }}
          onClick={() => setOpen(false)}
          data-testid="event-qr-modal"
        >
          <div className="bg-white rounded-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto text-center shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="overline mb-1">Event-QR-Code</div>
            <h3 className="font-display text-2xl mb-1" style={{ color: "var(--wp-olive)" }}>{tour.title}</h3>
            <p className="text-xs opacity-70 mb-4">Scannen → schnell anmelden (Name + optional E-Mail).</p>
            <div className="flex items-center justify-center mb-4 p-4 rounded-2xl" style={{ backgroundColor: "var(--wp-cream)" }}>
              {imgSrc ? (
                <img src={imgSrc} alt="QR-Code" className="w-64 h-64" data-testid="event-qr-image" />
              ) : (
                <div className="w-64 h-64 flex items-center justify-center text-xs opacity-60">Lade …</div>
              )}
            </div>
            <div className="text-[11px] opacity-60 mb-4 break-all">{checkinUrl}</div>
            <div className="flex flex-col gap-2">
              <button onClick={downloadPng} className="btn-primary justify-center" data-testid="event-qr-download">
                <Download className="w-4 h-4" /> PNG herunterladen
              </button>
              <a href={posterUrl} target="_blank" rel="noreferrer" className="btn-secondary justify-center" data-testid="event-qr-poster">
                <i className="fa-solid fa-print" /> Druck-Poster (A4)
              </a>
              <button onClick={copyLink} className="text-xs underline opacity-70 hover:opacity-100" data-testid="event-qr-copy">
                Anmelde-Link kopieren
              </button>
            </div>
            <button onClick={() => setOpen(false)} className="text-xs underline opacity-60 mt-3" data-testid="event-qr-close">
              Schließen
            </button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

function ToursAdmin() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiTone, setAiTone] = useState("warm");
  const [aiSeason, setAiSeason] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);

  const load = () => { setLoading(true); api.get("/admin/tours").then((r) => setList(r.data || [])).finally(() => setLoading(false)); };
  useEffect(load, []);

  const del = (id) => {
    if (!window.confirm("Tour löschen? Alle Buchungen werden ebenfalls gelöscht.")) return;
    api.delete(`/admin/tours/${id}`).then(() => {
      toast.success("Tour gelöscht");
      load();
    });
  };

  const save = async (e) => {
    e.preventDefault();
    const isTour = edit.kind === "tour";
    const parent = list.find((x) => x.id === edit.parent_id);
    const payload = {
      title: edit.title || (parent ? parent.title : ""),
      kind: edit.kind || "date",
      parent_id: isTour ? "" : (edit.parent_id || ""),
      slug: edit.slug || "",
      is_active: edit.is_active !== false,
      order: parseInt(edit.order) || 0,
      season_label: edit.season_label || "",
      short_description: edit.short_description || "",
      image_alt: edit.image_alt || "",
      gallery: parseGallery(edit._gallery_text ?? galleryToText(edit.gallery)),
      directions_notes: edit.directions_notes || "",
      description: edit.description || "",
      image_url: edit.image_url || "",
      date: isTour ? "" : edit.date,
      meeting_point: isTour ? "" : (edit.meeting_point || ""),
      duration_minutes: parseInt(edit.duration_minutes) || 120,
      price_eur: edit.is_free ? 0 : (parseFloat(edit.price_eur) || 0),
      is_free: !!edit.is_free,
      max_participants: parseInt(edit.max_participants) || 12,
      current_bookings: parseInt(edit.current_bookings) || 0,
      level: edit.level || "Einsteiger",
      booking_type: edit.booking_type || "internal",
      external_booking_url: edit.external_booking_url || "",
      booking_opens_at: edit.booking_opens_at || "",
      is_event: !!edit.is_event,
      partner_name: edit.partner_name || "",
      partner_logo_url: edit.partner_logo_url || "",
      event_social_link: edit.event_social_link || "",
      event_hashtag: edit.event_hashtag || "",
    };
    try {
      if (edit.id) await api.put(`/admin/tours/${edit.id}`, payload);
      else await api.post("/admin/tours", payload);
      toast.success("Gespeichert");
      setEdit(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const newDate = (parent) => {
    setEdit({
      kind: "date", parent_id: parent.id, title: parent.title, is_active: true,
      description: "", image_url: "", date: "", meeting_point: "", directions_notes: "",
      duration_minutes: 180, price_eur: 0, is_free: true, max_participants: 15, current_bookings: 0,
      level: "Einsteiger", booking_type: "internal", external_booking_url: "", booking_opens_at: "",
      is_event: false, partner_name: "", partner_logo_url: "", event_social_link: "", event_hashtag: "",
    });
    setAiOpen(false);
  };

  const newTour = () => {
    setEdit({
      kind: "tour", is_active: true, slug: "", season_label: "", short_description: "", image_alt: "", gallery: [], order: 0,
      title: "", description: "", image_url: "", date: "",
      meeting_point: "", duration_minutes: 180, price_eur: 0, is_free: true,
      max_participants: 15, current_bookings: 0, level: "Einsteiger",
      booking_type: "internal", external_booking_url: "", booking_opens_at: "",
      is_event: false, partner_name: "", partner_logo_url: "", event_social_link: "", event_hashtag: "",
    });
    setAiOpen(false);
  };

  // --- AI: generate description + plant list + image prompt ----------------
  const aiGenerate = async () => {
    if (!edit?.title) {
      toast.error("Bitte zuerst einen Titel eingeben");
      return;
    }
    setAiBusy(true);
    try {
      const r = await api.post("/admin/tours/ai-generate", {
        title: edit.title,
        location: edit.meeting_point || "",
        duration_minutes: parseInt(edit.duration_minutes) || 120,
        level: edit.level || "Einsteiger",
        season_hint: aiSeason,
        tone: aiTone,
      });
      const plantsTxt = (r.data.plants_seen || []).join(", ");
      const desc = r.data.description + (plantsTxt ? `\n\nPflanzen, die wir gemeinsam entdecken:\n${plantsTxt}` : "");
      setEdit((prev) => ({ ...prev, description: desc, _image_prompt: r.data.image_prompt }));
      toast.success(`Beschreibung + ${r.data.plants_seen?.length || 0} Pflanzen erstellt`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "KI-Fehler");
    } finally {
      setAiBusy(false);
    }
  };

  const aiTourImage = async () => {
    const prompt = edit?._image_prompt || edit?.title;
    if (!prompt) return toast.error("Bitte zuerst einen Titel eingeben");
    setImgBusy(true);
    try {
      const r = await api.post("/admin/tours/ai-image", { prompt, style: "photo" });
      setEdit((prev) => ({ ...prev, image_url: r.data.url }));
      toast.success("Tour-Bild generiert");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bild-Fehler");
    } finally {
      setImgBusy(false);
    }
  };

  if (loading) return <Spinner />;
  return (
    <div data-testid="admin-tours">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="text-sm opacity-70">{list.length} Touren</div>
        <button onClick={newTour} className="btn-primary !py-2 !px-4" data-testid="admin-new-tour"><Plus className="w-4 h-4" /> Neue Tour</button>
      </div>

      <div className="space-y-4">
        {sortTours(list).map((t) => (
          <div key={t.id} className={`plant-card p-5 flex gap-4 items-start ${t.kind === "date" ? "sm:ml-10" : ""} ${t.is_active === false ? "opacity-60" : ""}`} data-testid={`admin-tour-row-${t.id}`}>
            {t.image_url ? (
              <img src={t.image_url} alt={t.title} className="w-24 h-24 object-cover rounded-lg" />
            ) : (
              <div className="w-24 h-24 rounded-lg flex items-center justify-center text-xs opacity-50" style={{ backgroundColor: "var(--wp-cream)" }}>kein Bild</div>
            )}
            <div className="flex-1">
              <div className="flex items-start justify-between gap-2">
                <h4 className="font-display text-xl flex items-center gap-2 flex-wrap">
                  {t.title}
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: t.kind === "tour" ? "#1C2E1F" : "var(--wp-cream)", color: t.kind === "tour" ? "#F5EFE3" : "var(--wp-forest)" }} data-testid={`admin-tour-kind-${t.id}`}>
                    {t.kind === "tour" ? "Tour" : t.kind === "date" ? "Termin" : "Alt"}
                  </span>
                  {t.is_active === false && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ backgroundColor: "rgba(167,58,58,0.12)", color: "var(--wp-toxic)" }}>nicht öffentlich</span>}
                  {t.is_event && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: "var(--wp-olive)", color: "var(--wp-cream)" }} data-testid={`admin-tour-event-badge-${t.id}`}>
                      <i className="fa-solid fa-star text-[8px]" /> Event
                    </span>
                  )}
                </h4>
                <div className="flex gap-2 shrink-0 items-center">
                  {t.is_event && (
                    <EventQrButton tour={t} />
                  )}
                  {t.kind === "tour" && (
                    <button onClick={() => newDate(t)} className="text-xs px-3 py-1.5 rounded-full font-medium" style={{ backgroundColor: "var(--wp-sand)", color: "var(--wp-forest)" }} data-testid={`add-date-${t.id}`}><Plus className="w-3 h-3 inline" /> Termin</button>
                  )}
                  <button onClick={() => setEdit({ ...t, date: t.date?.slice(0, 16) })} className="text-[var(--wp-primary)]" data-testid={`edit-tour-${t.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => del(t.id)} className="text-[var(--wp-toxic)]" data-testid={`delete-tour-${t.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="text-xs opacity-70">
                {t.kind === "tour"
                  ? `${t.season_label || "–"} · /tours/${t.slug || ""}`
                  : `${t.date ? new Date(t.date).toLocaleString("de-DE") : "ohne Datum"} · ${t.meeting_point || "⚠ kein Treffpunkt"}`}
              </div>
              <div className="text-sm mt-1 flex items-center gap-2 flex-wrap">
                <span>{t.current_bookings}/{t.max_participants} Plätze</span>
                <span>·</span>
                <span>{t.is_free ? <span className="badge badge-edible">Gratis</span> : `${Number(t.price_eur).toFixed(2)} €`}</span>
                {t.booking_type === "external" && <span className="badge" style={{ background: "rgba(139,90,43,0.12)", color: "#8B5A2B" }}>Externe Buchung</span>}
              </div>
              {(t.bookings || []).length > 0 && (
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer">Teilnehmer ({t.bookings.length})</summary>
                  <ul className="mt-2 space-y-1">
                    {t.bookings.map((b) => <li key={b.id}>{b.name} ({b.email}) – {b.participants}p</li>)}
                  </ul>
                </details>
              )}
            </div>
          </div>
        ))}
      </div>

      {edit && (
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ backgroundColor: "rgba(26,47,28,0.6)" }} onClick={() => setEdit(null)}>
          <div className="bg-white rounded-t-3xl sm:rounded-2xl max-w-2xl w-full p-5 sm:p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4 mb-4">
              <h3 className="font-display text-2xl" style={{ color: "var(--wp-olive)" }}>{edit.kind === "date" ? (edit.id ? "Termin bearbeiten" : "Neuer Termin") : (edit.id ? "Tour bearbeiten" : "Neue Tour")}</h3>
              <button
                type="button"
                onClick={() => setAiOpen((v) => !v)}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium"
                style={{ backgroundColor: aiOpen ? "var(--wp-olive)" : "var(--wp-sand)", color: aiOpen ? "var(--wp-cream)" : "var(--wp-forest)" }}
                data-testid="tour-ai-toggle"
              >
                <i className="fa-solid fa-wand-magic-sparkles text-xs" /> KI-Assistent
              </button>
            </div>

            {aiOpen && (
              <div className="mb-4 p-4 rounded-xl border" style={{ borderColor: "var(--wp-border)", backgroundColor: "var(--wp-cream)" }} data-testid="tour-ai-panel">
                <div className="overline mb-2">KI-Beschreibung &amp; Pflanzenliste</div>
                <p className="text-xs opacity-70 mb-3">Trage erst Titel, Dauer und Niveau ein. Claude schreibt dann die Beschreibung und schlägt 6-12 Wildpflanzen vor, die ihr auf der Tour sehen könnt.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                  <input
                    placeholder="Saison/Monat (optional, z.B. Mai)"
                    value={aiSeason}
                    onChange={(e) => setAiSeason(e.target.value)}
                    className="px-3 py-2 rounded-lg border text-sm"
                    style={{ borderColor: "var(--wp-border)" }}
                    data-testid="tour-ai-season"
                  />
                  <select value={aiTone} onChange={(e) => setAiTone(e.target.value)} className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wp-border)" }}>
                    <option value="warm">Warm &amp; persönlich</option>
                    <option value="informativ">Informativ</option>
                    <option value="locker">Locker</option>
                  </select>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={aiGenerate}
                    disabled={aiBusy || !edit.title}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium"
                    style={{ backgroundColor: "var(--wp-olive)", color: "var(--wp-cream)", opacity: (aiBusy || !edit.title) ? 0.6 : 1 }}
                    data-testid="tour-ai-generate"
                  >
                    <i className="fa-solid fa-wand-magic-sparkles text-xs" />
                    {aiBusy ? "Claude schreibt …" : "Beschreibung + Pflanzen generieren"}
                  </button>
                  <button
                    type="button"
                    onClick={aiTourImage}
                    disabled={imgBusy || !edit.title}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium"
                    style={{ backgroundColor: "var(--wp-sand)", color: "var(--wp-forest)", opacity: (imgBusy || !edit.title) ? 0.6 : 1 }}
                    data-testid="tour-ai-image"
                  >
                    <i className="fa-solid fa-image text-xs" />
                    {imgBusy ? "Generiere …" : "KI-Tourbild"}
                  </button>
                </div>
              </div>
            )}

            <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <TourKindFields edit={edit} setEdit={setEdit} tours={list.filter((x) => x.kind === "tour")} />
              <label className="block">
                <div className="overline mb-1">Dauer (Minuten)</div>
                <input type="number" min="1" placeholder="120" value={edit.duration_minutes || ""} onChange={(e) => setEdit({ ...edit, duration_minutes: e.target.value })} className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} />
              </label>
              <label className="block">
                <div className="overline mb-1">Preis pro Person (€)</div>
                <input type="number" step="0.01" min="0" placeholder="35" value={edit.price_eur || ""} onChange={(e) => setEdit({ ...edit, price_eur: e.target.value })} className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} disabled={!!edit.is_free} />
              </label>
              <label className="block">
                <div className="overline mb-1">Max. Teilnehmer</div>
                <input type="number" min="1" placeholder="12" value={edit.max_participants || ""} onChange={(e) => setEdit({ ...edit, max_participants: e.target.value })} className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} />
              </label>
              <label className="block">
                <div className="overline mb-1">Schwierigkeitsgrad</div>
                <select value={edit.level || "Einsteiger"} onChange={(e) => setEdit({ ...edit, level: e.target.value })} className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }}>
                  <option>Einsteiger</option><option>Fortgeschritten</option><option>Experte</option>
                </select>
              </label>
              <label className="col-span-2 flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={!!edit.is_free} onChange={(e) => setEdit({ ...edit, is_free: e.target.checked, price_eur: e.target.checked ? 0 : edit.price_eur })} data-testid="tour-form-is-free" />
                Diese Tour ist kostenlos (Preisfeld wird gesperrt)
              </label>
              <div className="col-span-2">
                <div className="overline mb-2">Buchungsart</div>
                <div className="flex gap-4 text-sm">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="booking_type" checked={(edit.booking_type || "internal") === "internal"} onChange={() => setEdit({ ...edit, booking_type: "internal" })} data-testid="tour-form-booking-internal" />
                    Anmeldung über unsere Seite
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="booking_type" checked={edit.booking_type === "external"} onChange={() => setEdit({ ...edit, booking_type: "external" })} data-testid="tour-form-booking-external" />
                    Nur externer Link
                  </label>
                </div>
              </div>
              <label className="col-span-2 block">
                <div className="overline mb-1">Externer Buchungslink {edit.booking_type === "external" ? "*" : "(optional)"}</div>
                <input
                  type="url"
                  required={edit.booking_type === "external"}
                  placeholder="https://booking-anbieter.de/tour/..."
                  value={edit.external_booking_url || ""}
                  onChange={(e) => setEdit({ ...edit, external_booking_url: e.target.value, booking_type: e.target.value.trim() ? "external" : "internal" })}
                  className="w-full px-3 py-2 rounded-lg border"
                  style={{ borderColor: "var(--wp-border)" }}
                  data-testid="tour-form-external-url"
                />
                <div className="text-xs opacity-60 mt-1">{edit.kind === "tour" ? "Gilt für alle Termine dieser Tour, solange der Termin keinen eigenen Link hat." : "Ist ein Link eingetragen, wird nur noch darüber gebucht – die Anmeldung über unsere Seite ist dann aus."}</div>
              </label>
              {edit.booking_type !== "external" && (
                <label className="col-span-2 block">
                  <div className="overline mb-1 flex items-center gap-2">
                    <i className="fa-solid fa-lock-open text-xs opacity-60" />
                    Buchung öffnet am (optional)
                  </div>
                  <input
                    type="datetime-local"
                    value={edit.booking_opens_at || ""}
                    onChange={(e) => setEdit({ ...edit, booking_opens_at: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border"
                    style={{ borderColor: "var(--wp-border)" }}
                    data-testid="tour-form-booking-opens-at"
                  />
                  <div className="text-xs opacity-60 mt-1">Leer lassen für sofortige Buchbarkeit. Vor diesem Datum sehen Besucher die Tour, können aber noch nicht buchen.</div>
                </label>
              )}
              <div className="col-span-2">
                <div className="overline mb-2">Titelbild</div>
                <div className="flex items-center gap-3">
                  <ImageUpload value={edit.image_url} onChange={(v) => setEdit({ ...edit, image_url: v })} endpoint="/admin/uploads" testId="tour-image-upload" />
                  <input placeholder="oder Bild-URL einfügen" value={edit.image_url || ""} onChange={(e) => setEdit({ ...edit, image_url: e.target.value })} className="flex-1 px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} />
                </div>
              </div>
              <label className="col-span-2 block">
                <div className="overline mb-1">Beschreibung</div>
                <textarea placeholder="Was erwartet die Teilnehmer? Ablauf, Highlights, Ausrüstung…" value={edit.description || ""} onChange={(e) => setEdit({ ...edit, description: e.target.value })} className="w-full px-3 py-2 rounded-lg border min-h-[100px]" style={{ borderColor: "var(--wp-border)" }} />
              </label>

              {/* Event / Spezial-Tour */}
              <div className="col-span-2 mt-2 p-4 rounded-xl border" style={{ borderColor: "var(--wp-border)", backgroundColor: edit.is_event ? "var(--wp-cream)" : "transparent" }}>
                <label className="flex items-center gap-2 cursor-pointer mb-3">
                  <input
                    type="checkbox"
                    checked={!!edit.is_event}
                    onChange={(e) => setEdit({ ...edit, is_event: e.target.checked })}
                    data-testid="tour-form-is-event"
                  />
                  <span className="text-sm font-medium inline-flex items-center gap-1.5">
                    <i className="fa-solid fa-star text-xs" style={{ color: "var(--wp-olive)" }} />
                    Spezial-Event mit QR-Code-Anmeldung
                  </span>
                </label>
                {edit.is_event && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <input
                      placeholder="Partner-Name (z.B. Freiheitsmobile)"
                      value={edit.partner_name || ""}
                      onChange={(e) => setEdit({ ...edit, partner_name: e.target.value })}
                      className="px-3 py-2 rounded-lg border"
                      style={{ borderColor: "var(--wp-border)" }}
                      data-testid="tour-form-partner-name"
                    />
                    <input
                      placeholder="Hashtag (z.B. #freiheitsmobile)"
                      value={edit.event_hashtag || ""}
                      onChange={(e) => setEdit({ ...edit, event_hashtag: e.target.value })}
                      className="px-3 py-2 rounded-lg border"
                      style={{ borderColor: "var(--wp-border)" }}
                      data-testid="tour-form-event-hashtag"
                    />
                    <div className="col-span-2">
                      <div className="overline mb-1">Partner-Logo</div>
                      <div className="flex items-center gap-3">
                        <ImageUpload
                          value={edit.partner_logo_url}
                          onChange={(v) => setEdit({ ...edit, partner_logo_url: v })}
                          endpoint="/admin/uploads"
                          testId="tour-partner-logo-upload"
                        />
                        <input
                          placeholder="oder Logo-URL"
                          value={edit.partner_logo_url || ""}
                          onChange={(e) => setEdit({ ...edit, partner_logo_url: e.target.value })}
                          className="flex-1 px-3 py-2 rounded-lg border"
                          style={{ borderColor: "var(--wp-border)" }}
                          data-testid="tour-form-partner-logo-url"
                        />
                      </div>
                    </div>
                    <input
                      type="url"
                      placeholder="Event-Social-Link (Instagram, Facebook …)"
                      value={edit.event_social_link || ""}
                      onChange={(e) => setEdit({ ...edit, event_social_link: e.target.value })}
                      className="col-span-2 px-3 py-2 rounded-lg border"
                      style={{ borderColor: "var(--wp-border)" }}
                      data-testid="tour-form-event-social"
                    />
                    {edit.id && (
                      <div className="col-span-2 text-xs opacity-70 p-2 rounded" style={{ backgroundColor: "rgba(90,110,63,0.08)" }}>
                        <i className="fa-solid fa-qrcode mr-1" /> Nach dem Speichern findest du in der Touren-Liste einen QR-Code-Button zum Ausdrucken.
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="col-span-2 flex justify-end gap-2 mt-2">
                <button type="button" onClick={() => setEdit(null)} className="btn-secondary">Abbrechen</button>
                <button className="btn-primary" data-testid="tour-form-submit">Speichern</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function UsersAdmin() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);

  const load = () => { setLoading(true); api.get("/admin/users").then((r) => setList(r.data || [])).finally(() => setLoading(false)); };
  useEffect(load, []);

  const del = (id) => { if (!window.confirm("User löschen?")) return; api.delete(`/admin/users/${id}`).then(() => { toast.success("User gelöscht"); load(); }); };
  const reset = (id) => {
    const np = window.prompt("Neues Passwort (min. 6 Zeichen):");
    if (!np || np.length < 6) { toast.error("Passwort zu kurz"); return; }
    api.post("/admin/users/reset-password", { user_id: id, new_password: np }).then(() => {
      toast.success("Passwort gesetzt");
    });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/users/${edit.id}`, { name: edit.name, email: edit.email });
      toast.success("Gespeichert");
      setEdit(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler");
    }
  };

  if (loading) return <Spinner />;
  return (
    <div data-testid="admin-users">
      <div className="text-sm opacity-70 mb-4">{list.length} User</div>
      <div className="plant-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--wp-cream)] text-left"><tr><th className="p-3">Name</th><th className="p-3">E-Mail</th><th className="p-3">Erstellt</th><th className="p-3"></th></tr></thead>
          <tbody>
            {list.map((u) => (
              <tr key={u.id} className="border-t" style={{ borderColor: "var(--wp-border)" }}>
                <td className="p-3">{u.name}</td>
                <td className="p-3">{u.email}</td>
                <td className="p-3 text-xs opacity-70">{new Date(u.created_at).toLocaleDateString("de-DE")}</td>
                <td className="p-3 text-right">
                  <button onClick={() => setEdit({ ...u })} className="px-2 text-[var(--wp-primary)]" title="Bearbeiten" data-testid={`edit-user-${u.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => reset(u.id)} className="text-xs btn-secondary !py-1 !px-3 mx-1">PW reset</button>
                  <button onClick={() => del(u.id)} className="px-2 text-[var(--wp-toxic)]" data-testid={`delete-user-${u.id}`}><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {edit && (
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ backgroundColor: "rgba(26,47,28,0.6)" }} onClick={() => setEdit(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-2xl mb-4">User bearbeiten</h3>
            <form onSubmit={save} className="space-y-3 text-sm">
              <input required placeholder="Name" value={edit.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} data-testid="user-form-name" />
              <input required type="email" placeholder="E-Mail" value={edit.email || ""} onChange={(e) => setEdit({ ...edit, email: e.target.value })} className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} data-testid="user-form-email" />
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setEdit(null)} className="btn-secondary">Abbrechen</button>
                <button className="btn-primary" data-testid="user-form-submit">Speichern</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function BookingEditButton({ booking, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(booking);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setForm(booking); }, [open, booking]);

  const save = (e) => {
    e.preventDefault();
    if (!form.name?.trim()) { toast.error("Name fehlt"); return; }
    setBusy(true);
    api.put(`/admin/bookings/${booking.id}`, {
      name: form.name,
      email: form.email,
      phone: form.phone,
      participants: parseInt(form.participants) || 1,
      message: form.message,
    })
      .then(() => { toast.success("Buchung aktualisiert"); setOpen(false); onSaved(); })
      .catch((err) => toast.error(err?.response?.data?.detail || "Fehler beim Speichern"))
      .finally(() => setBusy(false));
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="opacity-60 hover:opacity-100 p-1 shrink-0"
        title="Buchung bearbeiten"
        data-testid={`edit-booking-${booking.id}`}
      >
        <Pencil className="w-4 h-4" />
      </button>
      {open && createPortal(
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ backgroundColor: "rgba(26,47,28,0.6)" }} onClick={() => setOpen(false)} data-testid="booking-edit-modal">
          <form onSubmit={save} className="bg-white rounded-t-3xl sm:rounded-2xl max-w-md w-full p-5 sm:p-7 max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs mb-3" style={{ backgroundColor: "var(--wp-cream)", color: "var(--wp-olive)" }}>
              <Pencil className="w-3.5 h-3.5" /> Buchung bearbeiten
            </div>
            <h3 className="font-display text-xl mb-1">{booking.tour_title}</h3>
            <p className="text-xs opacity-60 mb-4">Änderungen werden sofort übernommen. Die Teilnehmer-Zahl der Tour passt sich automatisch an.</p>
            <div className="space-y-3">
              <input required placeholder="Name *" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wp-border)" }} data-testid="booking-edit-name" autoFocus />
              <input type="email" placeholder="E-Mail" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wp-border)" }} data-testid="booking-edit-email" />
              <input placeholder="Telefon" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wp-border)" }} data-testid="booking-edit-phone" />
              <div>
                <label className="text-[10px] opacity-60 uppercase tracking-wider">Teilnehmer-Anzahl</label>
                <input type="number" min="1" max="50" value={form.participants || 1} onChange={(e) => setForm({ ...form, participants: parseInt(e.target.value) || 1 })} className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wp-border)" }} data-testid="booking-edit-participants" />
              </div>
              <textarea placeholder="Nachricht / Notiz" value={form.message || ""} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full px-3 py-2 rounded-lg border text-sm min-h-[80px]" style={{ borderColor: "var(--wp-border)" }} />
            </div>
            <div className="flex gap-2 justify-end mt-5">
              <button type="button" onClick={() => setOpen(false)} className="btn-secondary !py-1.5 !px-4 text-sm">Abbrechen</button>
              <button type="submit" disabled={busy} className="btn-primary !py-1.5 !px-4 text-sm" data-testid="booking-edit-save">
                {busy ? "Speichere…" : "Speichern"}
              </button>
            </div>
          </form>
        </div>,
        document.body,
      )}
    </>
  );
}

function ManualBookingButton({ tour, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", participants: 1, message: "", skip_email: true });
  const [busy, setBusy] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name fehlt"); return; }
    setBusy(true);
    api.post("/admin/bookings", { tour_id: tour.tour_id, ...form })
      .then(() => {
        toast.success(`${form.name} eingetragen für ${tour.title}`);
        setForm({ name: "", email: "", phone: "", participants: 1, message: "", skip_email: true });
        setOpen(false);
        onCreated();
      })
      .catch((err) => toast.error(err?.response?.data?.detail || "Konnte nicht eintragen"))
      .finally(() => setBusy(false));
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
        style={{ backgroundColor: "var(--wp-olive)", color: "var(--wp-cream)" }}
        data-testid={`manual-booking-${tour.tour_id}`}
      >
        <i className="fa-solid fa-user-plus text-xs" /> Manuell anmelden
      </button>
      {open && createPortal(
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ backgroundColor: "rgba(26,47,28,0.6)" }} onClick={() => setOpen(false)} data-testid="manual-booking-modal">
          <form onSubmit={submit} className="bg-white rounded-t-3xl sm:rounded-2xl max-w-md w-full p-5 sm:p-7 max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs mb-3" style={{ backgroundColor: "var(--wp-cream)", color: "var(--wp-olive)" }}>
              <i className="fa-solid fa-user-plus" /> Manuelle Anmeldung
            </div>
            <h3 className="font-display text-xl mb-1">{tour.title}</h3>
            <p className="text-xs opacity-60 mb-4">Nutze das für Anmeldungen, die du per Telefon, E-Mail oder vor Ort bekommst. Die Person wird zur Tour gezählt und erscheint im Admin-Kalender.</p>
            <div className="space-y-3">
              <input required placeholder="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wp-border)" }} data-testid="manual-name" autoFocus />
              <input type="email" placeholder="E-Mail (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wp-border)" }} data-testid="manual-email" />
              <input placeholder="Telefon (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wp-border)" }} data-testid="manual-phone" />
              <input type="number" min="1" max="20" value={form.participants} onChange={(e) => setForm({ ...form, participants: parseInt(e.target.value) || 1 })} className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wp-border)" }} data-testid="manual-participants" />
              <textarea placeholder={'Interne Notiz (optional, z.B. «Allergie auf Nüsse")'} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full px-3 py-2 rounded-lg border text-sm min-h-[70px]" style={{ borderColor: "var(--wp-border)" }} />
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={!form.skip_email} onChange={(e) => setForm({ ...form, skip_email: !e.target.checked })} disabled={!form.email} data-testid="manual-send-email" />
                <span className={form.email ? "" : "opacity-40"}>Bestätigungs-E-Mail an die Person senden{!form.email && " (E-Mail nötig)"}</span>
              </label>
            </div>
            <div className="flex gap-2 justify-end mt-5">
              <button type="button" onClick={() => setOpen(false)} className="btn-secondary !py-1.5 !px-4 text-sm">Abbrechen</button>
              <button type="submit" disabled={busy} className="btn-primary !py-1.5 !px-4 text-sm" data-testid="manual-submit">
                {busy ? "Trage ein…" : "Eintragen"}
              </button>
            </div>
          </form>
        </div>,
        document.body,
      )}
    </>
  );
}

function AdminCalendarSubscribeCard() {
  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const [feedToken, setFeedToken] = useState("");
  useEffect(() => { api.get("/admin/calendar-feed-token").then((r) => setFeedToken(r.data.token)).catch(() => {}); }, []);
  const feedUrl = feedToken ? `${apiBase}/api/admin/calendar.ics?token=${encodeURIComponent(feedToken)}` : "";
  const webcalUrl = feedUrl.replace(/^https?:/, "webcal:");
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(feedUrl);
      setCopied(true);
      toast.success("Kalender-URL kopiert");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Konnte nicht kopieren");
    }
  };

  return (
    <div className="plant-card p-5" style={{ borderLeft: "4px solid var(--wp-olive)" }} data-testid="admin-calendar-subscribe">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="text-3xl shrink-0 select-none">📅</div>
        <div className="flex-1 min-w-[240px]">
          <div className="overline mb-1" style={{ color: "var(--wp-olive)" }}>Admin-Kalender (live auf deinem iPhone)</div>
          <h3 className="font-display text-xl mb-2">Touren-Feed abonnieren</h3>
          <p className="text-sm leading-relaxed opacity-80 mb-3">
            Abonniere diesen ICS-Feed in der iPhone/Samsung-Kalender-App. Alle Touren erscheinen automatisch in deinem Kalender — inklusive Teilnehmer-Liste mit Name, Telefon und E-Mail. Aktualisiert sich von selbst, sobald jemand bucht.
          </p>
          <div className="flex flex-wrap gap-2 mb-3">
            <a
              href={webcalUrl}
              className="btn-primary !py-2 !px-4 text-sm"
              data-testid="admin-calendar-subscribe-btn"
              title='Öffnet die Kalender-App auf iPhone/Mac und fragt: «Abonnieren?"'
            >
              <i className="fa-solid fa-mobile-screen" /> Direkt auf iPhone abonnieren
            </a>
            <button onClick={copy} className="btn-secondary !py-2 !px-4 text-sm" data-testid="admin-calendar-copy-url">
              {copied ? <><i className="fa-solid fa-check" /> Kopiert</> : <><i className="fa-solid fa-link" /> URL kopieren</>}
            </button>
          </div>
          <details className="text-xs opacity-70">
            <summary className="cursor-pointer hover:opacity-100">Anleitung: Auf Samsung / Android / Google-Kalender abonnieren</summary>
            <ol className="mt-2 space-y-1 pl-5 list-decimal">
              <li>{`URL oben kopieren («URL kopieren").`}</li>
              <li>Auf dem Computer: <code className="text-[10px]">calendar.google.com</code> {`öffnen → links bei «Weitere Kalender" das + klicken → «Per URL" → URL einfügen → «Kalender hinzufügen".`}</li>
              <li>Der Kalender erscheint dann automatisch in der Google-Kalender-App auf deinem Samsung-Handy.</li>
              <li>Alternativ: In iCloud (auf macOS) abonnieren → wird auf iPhone synchronisiert.</li>
            </ol>
            <div className="mt-2 text-[10px] opacity-60 font-mono break-all" data-testid="admin-calendar-url-display">{feedUrl}</div>
          </details>
        </div>
      </div>
    </div>
  );
}

function BookingsAdmin() {
  const [list, setList] = useState([]);
  const [allTours, setAllTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/bookings").then((r) => r.data || []),
      api.get("/admin/tours").then((r) => r.data || []),
    ]).then(([b, t]) => { setList(b); setAllTours(t); }).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const del = (id) => {
    if (!window.confirm("Buchung löschen?")) return;
    api.delete(`/admin/bookings/${id}`).then(() => {
      toast.success("Buchung gelöscht");
      load();
    });
  };

  if (loading) return <Spinner />;

  // Group bookings by tour for attendance / calendar actions
  const byTour = list.reduce((acc, b) => {
    const key = b.tour_id || "_";
    if (!acc[key]) acc[key] = { title: b.tour_title || "—", tour_id: b.tour_id, entries: [], total_people: 0 };
    acc[key].entries.push(b);
    acc[key].total_people += Number(b.participants || 1);
    return acc;
  }, {});
  const tourGroups = Object.values(byTour).sort((a, b) => (a.title || "").localeCompare(b.title || ""));

  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const adminHref = (tid, suffix) => {
    return `${apiBase}/api/admin/tours/${tid}/${suffix}`;
  };

  // For PDFs the file is served with X-Admin-Pin via fetch and opened in new tab
  const openWithPin = async (url, filename) => {
    try {
      const r = await api.get(url, { responseType: "blob" });
      const blob = new Blob([r.data]);
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      if (filename) a.download = filename;
      else a.target = "_blank";
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error("Download fehlgeschlagen");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-bookings">
      <AdminCalendarSubscribeCard />
      <div className="text-sm opacity-70 mb-2">{list.length} Buchungen · {tourGroups.length} Touren</div>
      {list.length === 0 && <div className="text-sm opacity-70">Noch keine Buchungen.</div>}

      {tourGroups.map((g) => (
        <div key={g.tour_id} className="plant-card p-4" data-testid={`bookings-tour-${g.tour_id}`}>
          <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
            <div>
              <div className="font-display text-lg">{g.title}</div>
              <div className="text-xs opacity-70">{g.entries.length} Buchung{g.entries.length === 1 ? "" : "en"} · {g.total_people} Person{g.total_people === 1 ? "" : "en"}</div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <ManualBookingButton tour={g} onCreated={load} />
              <button
                onClick={() => openWithPin(`/admin/tours/${g.tour_id}/attendance.pdf`, `praesenzliste-${g.tour_id}.pdf`)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border"
                style={{ borderColor: "var(--wp-olive)", color: "var(--wp-olive)" }}
                data-testid={`download-attendance-${g.tour_id}`}
                title="Druckbare A4-Präsenzliste mit Anwesend/Bezahlt-Checkboxen"
              >
                <i className="fa-solid fa-clipboard-list text-xs" /> Präsenzliste PDF
              </button>
              <button
                onClick={() => openWithPin(`/admin/tours/${g.tour_id}/calendar.ics`, `tour-${g.tour_id}.ics`)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border"
                style={{ borderColor: "var(--wp-olive)", color: "var(--wp-olive)" }}
                data-testid={`download-ics-${g.tour_id}`}
                title="ICS für iPhone / Samsung / Google Kalender"
              >
                <i className="fa-solid fa-calendar-plus text-xs" /> Kalender (ICS)
              </button>
            </div>
          </div>
          <div className="space-y-2">
            {g.entries.map((b) => (
              <div key={b.id} className="text-sm flex items-start justify-between gap-3 p-2 rounded-lg" style={{ backgroundColor: "var(--wp-cream)" }}>
                <div className="flex-1">
                  <div className="font-medium">{b.name} <span className="text-xs opacity-70 font-normal">· {b.participants} Personen</span></div>
                  <div className="text-xs opacity-70">{b.email}{b.phone && ` · ${b.phone}`} · {new Date(b.created_at).toLocaleString("de-DE")}</div>
                  {b.message && <div className="mt-1 text-xs italic">{`«${b.message}"`}</div>}
                </div>
                <BookingEditButton booking={b} onSaved={load} />
                <button onClick={() => del(b.id)} className="text-[var(--wp-toxic)] shrink-0 p-1" data-testid={`delete-booking-${b.id}`} title="Buchung löschen"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Tours that have no bookings yet — show them so admin can manually add */}
      {(() => {
        const booked = new Set(tourGroups.map((g) => g.tour_id));
        const future = allTours.filter((t) => !booked.has(t.id) && new Date(t.date) > new Date(Date.now() - 86400000));
        if (future.length === 0) return null;
        return (
          <div className="plant-card p-4" data-testid="bookings-empty-tours">
            <div className="overline mb-2 opacity-70">Touren ohne Anmeldungen ({future.length})</div>
            <p className="text-xs opacity-60 mb-3">Diese Touren haben noch keine Buchungen. Du kannst hier direkt jemanden manuell eintragen.</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {future.map((t) => (
                <div key={t.id} className="flex items-center justify-between gap-3 p-2 rounded-lg" style={{ backgroundColor: "var(--wp-cream)" }}>
                  <div className="text-sm flex-1 min-w-0">
                    <div className="font-medium truncate">{t.title}</div>
                    <div className="text-[10px] opacity-60">{new Date(t.date).toLocaleString("de-DE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
                  </div>
                  <ManualBookingButton tour={{ tour_id: t.id, title: t.title }} onCreated={load} />
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function WaitlistAdmin() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = () => {
    setLoading(true);
    api.get("/admin/waitlist").then((r) => setList(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const del = (id) => {
    if (!window.confirm("Eintrag von der Warteliste entfernen?")) return;
    api.delete(`/admin/waitlist/${id}`)
      .then(() => { toast.success("Entfernt"); load(); })
      .catch(() => toast.error("Löschen fehlgeschlagen"));
  };

  const notify = async (id) => {
    if (!confirm("Soll dieser Person jetzt eine 'Platz frei'-E-Mail geschickt werden?")) return;
    setBusy((s) => ({ ...s, [id]: true }));
    try {
      await api.post(`/admin/waitlist/${id}/notify`);
      toast.success("Benachrichtigung verschickt");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Versand fehlgeschlagen");
    } finally {
      setBusy((s) => ({ ...s, [id]: false }));
    }
  };

  // Group by tour
  const byTour = list.reduce((acc, w) => {
    if (!acc[w.tour_id]) acc[w.tour_id] = { title: w.tour_title, entries: [] };
    acc[w.tour_id].entries.push(w);
    return acc;
  }, {});

  if (loading) return <Spinner />;

  return (
    <div className="space-y-5" data-testid="admin-waitlist">
      <div className="text-sm opacity-70">{list.length} Einträge auf Wartelisten · {Object.keys(byTour).length} Touren mit Warteliste</div>
      {list.length === 0 && (
        <div className="plant-card p-8 text-center text-sm opacity-70">Aktuell ist niemand auf einer Warteliste.</div>
      )}
      {Object.entries(byTour).map(([tourId, { title, entries }]) => (
        <div key={tourId} className="plant-card p-4" data-testid={`waitlist-tour-${tourId}`}>
          <div className="font-display text-lg mb-3 flex items-center gap-2">
            <i className="fa-solid fa-bell-concierge text-sm" style={{ color: "var(--wp-olive)" }} />
            {title} <span className="text-xs opacity-60 font-body">({entries.length})</span>
          </div>
          <div className="space-y-2">
            {entries.map((w) => (
              <div key={w.id} className="flex items-start justify-between gap-3 p-3 rounded-lg" style={{ backgroundColor: "var(--wp-cream)" }} data-testid={`waitlist-entry-${w.id}`}>
                <div className="flex-1 text-sm">
                  <div className="font-medium">
                    {w.name}
                    {w.status === "notified" && <span className="ml-2 inline-block text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: "var(--wp-olive)", color: "var(--wp-cream)" }}>benachrichtigt</span>}
                  </div>
                  <div className="text-xs opacity-70">{w.email}{w.phone && ` · ${w.phone}`} · {w.participants} Person{w.participants > 1 ? "en" : ""} · {new Date(w.created_at).toLocaleString("de-DE")}</div>
                  {w.message && <div className="mt-1 text-xs italic">{`«${w.message}"`}</div>}
                </div>
                <div className="flex gap-1 shrink-0">
                  <button
                    onClick={() => notify(w.id)}
                    disabled={busy[w.id]}
                    className="px-3 py-1.5 rounded-full text-xs font-medium border"
                    style={{ borderColor: "var(--wp-olive)", color: "var(--wp-olive)" }}
                    title="Platz-frei-Mail senden"
                    data-testid={`notify-waitlist-${w.id}`}
                  >
                    {busy[w.id] ? "Sende…" : "Platz anbieten"}
                  </button>
                  <button onClick={() => del(w.id)} className="text-[var(--wp-toxic)] p-2" title="Entfernen" data-testid={`delete-waitlist-${w.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ContactsAdmin() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => { setLoading(true); api.get("/admin/contacts").then((r) => setList(r.data || [])).finally(() => setLoading(false)); };
  useEffect(load, []);

  const del = (id) => {
    if (!window.confirm("Nachricht löschen?")) return;
    api.delete(`/admin/contacts/${id}`).then(() => {
      toast.success("Nachricht gelöscht");
      load();
    });
  };

  if (loading) return <Spinner />;
  return (
    <div className="space-y-3" data-testid="admin-contacts">
      <div className="text-sm opacity-70 mb-2">{list.length} Nachrichten</div>
      {list.length === 0 && <div className="text-sm opacity-70">Keine Nachrichten.</div>}
      {list.map((c) => (
        <div key={c.id} className="plant-card p-4 text-sm flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="font-medium">{c.name} <span className="text-xs opacity-60">– {c.email}</span></div>
            <div className="text-xs opacity-60">{new Date(c.created_at).toLocaleString("de-DE")}</div>
            <p className="mt-2 whitespace-pre-wrap">{c.message}</p>
          </div>
          <button onClick={() => del(c.id)} className="text-[var(--wp-toxic)] shrink-0" data-testid={`delete-contact-${c.id}`}><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
    </div>
  );
}

function NewsletterAdmin() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [adding, setAdding] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/newsletter/subscribers").then((r) => setSubs(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const sendNow = async () => {
    if (!confirm("Newsletter jetzt an alle aktiven Abonnenten senden?")) return;
    setSending(true);
    try {
      const r = await api.post("/admin/newsletter/send");
      toast.success(`Newsletter versendet: ${r.data.sent} Empfänger (${r.data.plant || "—"})`);
    } catch {
      toast.error("Versand fehlgeschlagen");
    } finally {
      setSending(false);
    }
  };

  const add = async (e) => {
    e.preventDefault();
    if (!newEmail) return;
    setAdding(true);
    try {
      const r = await api.post("/admin/newsletter/subscribers", { email: newEmail });
      toast.success(r.data.reactivated ? "Reaktiviert" : "Hinzugefügt");
      setNewEmail("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler");
    } finally {
      setAdding(false);
    }
  };

  const del = async (email) => {
    if (!confirm(`Abonnent ${email} löschen?`)) return;
    await api.delete(`/admin/newsletter/subscribers/${encodeURIComponent(email)}`);
    toast.success("Gelöscht");
    load();
  };

  if (loading) return <Spinner />;

  const active = subs.filter((s) => s.active);

  return (
    <div className="space-y-6" data-testid="admin-newsletter">
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="plant-card p-5"><div className="overline mb-1">Aktiv</div><div className="font-display text-3xl">{active.length}</div></div>
        <div className="plant-card p-5"><div className="overline mb-1">Gesamt</div><div className="font-display text-3xl">{subs.length}</div></div>
        <div className="plant-card p-5 flex flex-col justify-between">
          <div className="overline">Nächster Versand</div>
          <div className="text-sm opacity-70 mb-2">Montag 09:00 Europe/Luxembourg (automatisch)</div>
          <button onClick={sendNow} disabled={sending || active.length === 0} className="btn-primary w-full justify-center" data-testid="newsletter-send-now">
            <Send className="w-4 h-4" /> {sending ? "Sende…" : "Jetzt senden"}
          </button>
        </div>
      </div>

      <form onSubmit={add} className="plant-card p-4 flex items-center gap-2 flex-wrap" data-testid="newsletter-add-form">
        <div className="overline mr-2">Abonnent hinzufügen:</div>
        <input type="email" required placeholder="email@example.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className="flex-1 min-w-[200px] px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wp-border)" }} data-testid="newsletter-add-input" />
        <button disabled={adding} className="btn-primary !py-2 !px-4" data-testid="newsletter-add-submit"><Plus className="w-4 h-4" /> Hinzufügen</button>
      </form>

      <div className="plant-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--wp-cream)] text-left"><tr><th className="p-3">E-Mail</th><th className="p-3">Status</th><th className="p-3">Abonniert</th><th className="p-3"></th></tr></thead>
          <tbody>
            {subs.length === 0 && <tr><td colSpan="4" className="p-6 text-center opacity-60">Noch keine Abonnenten</td></tr>}
            {subs.map((s) => (
              <tr key={s.email} className="border-t" style={{ borderColor: "var(--wp-border)" }}>
                <td className="p-3">{s.email}</td>
                <td className="p-3">{s.active ? <span className="badge badge-edible">Aktiv</span> : <span className="badge" style={{ background: "rgba(0,0,0,0.06)" }}>Abgemeldet</span>}</td>
                <td className="p-3 text-xs opacity-70">{new Date(s.created_at).toLocaleDateString("de-DE")}</td>
                <td className="p-3 text-right">
                  <button onClick={() => del(s.email)} className="text-[var(--wp-toxic)]" data-testid={`delete-subscriber-${s.email}`}><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EmbedSnippets() {
  const origin = window.location.origin;
  const [copied, setCopied] = useState("");
  const snippets = [
    {
      key: "tours",
      label: "Touren-Kalender (mit Buchungs-Link)",
      desc: "Zeigt die nächsten 6 Touren als Kacheln. Klick führt zu Buchung (intern oder extern). Falls keine Touren geplant sind, erscheint ein freundlicher Hinweis-Text.",
      code: `<iframe src="${origin}/embed/tours?limit=6" width="100%" height="520" frameborder="0" style="border:0;border-radius:12px;"></iframe>`,
    },
    {
      key: "booking",
      label: "Buchungssystem (als Liste)",
      desc: "Vertikale Liste aller buchbaren Touren – kompakter, für schmale Sidebars geeignet.",
      code: `<iframe src="${origin}/embed/booking?mode=book&limit=6" width="100%" height="600" frameborder="0" style="border:0;border-radius:12px;"></iframe>`,
    },
    {
      key: "plants",
      label: "Pflanzen-Vorschau (9 Wildkräuter)",
      desc: "Grid mit 9 essbaren/heilenden Pflanzen aus deiner DB.",
      code: `<iframe src="${origin}/embed/plants" width="100%" height="540" frameborder="0" style="border:0;border-radius:12px;"></iframe>`,
    },
  ];

  const copy = async (key, code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(key);
      toast.success("Code kopiert!");
      setTimeout(() => setCopied(""), 2000);
    } catch {
      toast.error("Konnte nicht kopieren");
    }
  };

  return (
    <div className="space-y-4" data-testid="embed-snippets">
      <p className="text-xs opacity-70">
        Kopiere diesen HTML-Code und füge ihn in deine externe Website (z.B. WordPress, Wix, Squarespace) ein.
      </p>
      {snippets.map((s) => (
        <div key={s.key} className="border rounded-xl p-4" style={{ borderColor: "var(--wp-border)" }}>
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="font-medium text-sm">{s.label}</div>
            <button
              type="button"
              onClick={() => copy(s.key, s.code)}
              className="btn-secondary !py-1 !px-3 text-xs"
              data-testid={`embed-copy-${s.key}`}
            >
              {copied === s.key ? "✓ Kopiert" : "Kopieren"}
            </button>
          </div>
          <p className="text-xs opacity-70 mb-2">{s.desc}</p>
          <pre className="text-[11px] bg-[var(--wp-cream)] p-2 rounded overflow-x-auto font-mono">{s.code}</pre>
          <a href={s.code.match(/src="([^"]+)"/)[1]} target="_blank" rel="noopener noreferrer" className="text-xs underline mt-2 inline-block opacity-70 hover:opacity-100">
            → Vorschau öffnen
          </a>
        </div>
      ))}
    </div>
  );
}



function RejectPhotoButton({ onReject, testId, className = "" }) {
  const [busy, setBusy] = useState(false);
  return (
    <button type="button" disabled={busy}
      onClick={async () => { setBusy(true); try { await onReject(); } finally { setBusy(false); } }}
      className={`mt-1 w-full inline-flex items-center justify-center gap-1 px-2 py-1 rounded-full border text-[11px] transition-colors hover:bg-[var(--wp-cream)] disabled:opacity-50 ${className}`}
      style={{ borderColor: "var(--wp-border)" }} data-testid={testId} title="Löscht das Foto und sucht automatisch ein besseres auf Wikimedia">
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Falsches Foto
    </button>
  );
}

const GALLERY_SLOTS = [["", "– Bildart –"], ["blatt", "Blatt"], ["bluete", "Blüte"], ["frucht", "Frucht"], ["standort", "Standort"], ["detail", "Detail"], ["galerie", "ohne"]];

function initialSlots(plant) {
  const m = {};
  Object.entries(plant?.photo_parts || {}).forEach(([k, u]) => { m[u] = k; });
  (plant?.image_labels || []).forEach((l) => { m[l.url] = l.slot; });
  (plant?.image_credits || []).forEach((c) => { if (c.slot) m[c.url] = c.slot; });
  return m;
}

function PlantGallery({ images, onChange, plant, onReject }) {
  const [newSlot, setNewSlot] = useState("");
  const [slots, setSlots] = useState(() => initialSlots(plant));
  const slotOf = (img) => slots[img] || Object.entries(slots).find(([u]) => u && img.endsWith(u))?.[1] || "";
  const saveSlot = async (url, slot) => {
    if (!plant?.id || !slot) return;
    try {
      await api.post("/admin/photo-review/slot", { plant_id: plant.id, url, slot });
      setSlots((s) => ({ ...s, [url]: slot }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bildart konnte nicht gespeichert werden");
    }
  };
  const add = (url) => {
    if (!url) return;
    onChange([...(images || []), url]);
    if (newSlot) saveSlot(url, newSlot);
  };
  const removeAt = (i) => onChange(images.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const next = [...images];
    const target = i + dir;
    if (target < 0 || target >= next.length) return;
    [next[i], next[target]] = [next[target], next[i]];
    onChange(next);
  };
  return (
    <div className="space-y-3" data-testid="plant-gallery">
      <div className="flex items-center gap-3 flex-wrap">
        {plant?.id && (
          <select value={newSlot} onChange={(e) => setNewSlot(e.target.value)} className="px-3 py-2 rounded-lg border text-xs bg-white" style={{ borderColor: "var(--wp-border)" }} data-testid="gallery-new-slot" title="Bildart für das nächste hochgeladene Bild">
            {GALLERY_SLOTS.map(([v, l]) => <option key={v} value={v}>{v ? `Neues Bild: ${l}` : "Bildart für neues Bild …"}</option>)}
          </select>
        )}
        <ImageUpload value="" onChange={add} endpoint="/admin/uploads" testId="plant-gallery-upload" stockQuery={plant?.name_lat || plant?.name_de || ""} plantId={plant?.id || ""} />
        <span className="text-xs opacity-60">oder URL:</span>
        <input
          placeholder="https://…"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(e.target.value); e.target.value = ""; } }}
          className="flex-1 min-w-[180px] px-3 py-2 rounded-lg border text-xs"
          style={{ borderColor: "var(--wp-border)" }}
          data-testid="plant-gallery-url-input"
        />
      </div>
      {plant && plant.name_de && (
        <AIPlantImageButton plant={plant} onGenerated={add} label="KI-Bild für Galerie (letzte Option)" small />
      )}
      {(images || []).length === 0 && <div className="text-xs opacity-50">Noch keine zusätzlichen Bilder.</div>}
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {(images || []).map((img, i) => (
          <div key={i} data-testid={`gallery-img-${i}`}>
            <div className="relative group">
            <img src={img} alt={`Galerie ${i + 1}`} className="w-full aspect-square object-cover rounded-lg" />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors rounded-lg flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="bg-white/90 rounded-full w-7 h-7 text-xs disabled:opacity-30" title="Nach links">←</button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === images.length - 1} className="bg-white/90 rounded-full w-7 h-7 text-xs disabled:opacity-30" title="Nach rechts">→</button>
              <button type="button" onClick={() => removeAt(i)} className="bg-white/90 rounded-full w-7 h-7 text-xs text-[var(--wp-toxic)]" title="Löschen" data-testid={`gallery-del-${i}`}>✕</button>
            </div>
            </div>
            {plant?.id && (
              <select value={slotOf(img)} onChange={(e) => saveSlot(img, e.target.value)} className="mt-1 w-full px-1 py-0.5 rounded border text-[11px] bg-white" style={{ borderColor: "var(--wp-border)" }} data-testid={`gallery-slot-${i}`}>
                {GALLERY_SLOTS.map(([v, l]) => <option key={v} value={v} disabled={!v}>{l}</option>)}
              </select>
            )}
            {onReject && <RejectPhotoButton onReject={() => onReject(img)} testId={`gallery-reject-${i}`} />}
          </div>
        ))}
      </div>
    </div>
  );
}

const PLANT_PARTS = [
  { v: "ganze", l: "Ganze Pflanze" },
  { v: "blatt", l: "Blatt" },
  { v: "bluete", l: "Blüte" },
  { v: "frucht", l: "Frucht" },
  { v: "standort", l: "Standort" },
  { v: "detail", l: "Detail" },
];

function BulkAIFillButton({ onDone }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [open, setOpen] = useState(false);

  const FIELDS = [
    { key: "historical_background", label: "Historischer Hintergrund" },
    { key: "look_alikes", label: "Verwechslungen" },
    { key: "warnings", label: "Warnungen" },
    { key: "uses", label: "Verwendung" },
    { key: "habitat", label: "Standort" },
    { key: "ingredients", label: "Inhaltsstoffe" },
    { key: "description", label: "Beschreibung" },
    { key: "short_description", label: "Kurz-Beschreibung" },
  ];
  const [selected, setSelected] = useState(new Set(["historical_background"]));

  const toggle = (k) => {
    const s = new Set(selected);
    if (s.has(k)) s.delete(k); else s.add(k);
    setSelected(s);
  };

  const run = async () => {
    if (selected.size === 0) {
      toast.error("Mindestens ein Feld auswählen");
      return;
    }
    if (!window.confirm(`Soll Claude für ALLE Pflanzen die ausgewählten Felder (${selected.size}) füllen, sofern sie leer sind? Das kann 1-3 Minuten dauern und verbraucht LLM-Budget.`)) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/admin/plants/ai-fill-all", { fields: Array.from(selected) });
      setResult(r.data);
      toast.success(`${r.data.updated_plants} von ${r.data.processed} Pflanzen aktualisiert`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk-Fill fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border"
        style={{ borderColor: "var(--wp-olive)", color: "var(--wp-olive)", backgroundColor: "var(--wp-cream)" }}
        data-testid="bulk-aifill-toggle"
      >
        <i className="fa-solid fa-wand-magic-sparkles text-xs" /> KI Bulk-Fill
      </button>
      {open && (
        <div className="absolute z-30 right-0 mt-2 w-80 max-w-[90vw] rounded-xl border bg-white shadow-xl p-4" style={{ borderColor: "var(--wp-border)" }} data-testid="bulk-aifill-menu">
          <div className="overline mb-2">Welche Felder befüllen?</div>
          <p className="text-xs opacity-60 mb-3">Claude füllt jeweils nur die ausgewählten Felder, die bei einer Pflanze leer sind. Vorhandene Inhalte werden nicht überschrieben.</p>
          <div className="space-y-1 mb-3 max-h-56 overflow-y-auto">
            {FIELDS.map((f) => (
              <label key={f.key} className="flex items-center gap-2 text-sm py-1 cursor-pointer">
                <input type="checkbox" checked={selected.has(f.key)} onChange={() => toggle(f.key)} data-testid={`bulk-field-${f.key}`} />
                <span>{f.label}</span>
              </label>
            ))}
          </div>
          <button onClick={run} disabled={busy} className="btn-primary !py-2 w-full justify-center" data-testid="bulk-aifill-run">
            {busy ? <><RefreshCw className="w-4 h-4 animate-spin" /> Claude arbeitet …</> : <>Jetzt starten ({selected.size} Felder)</>}
          </button>
          {result && (
            <div className="mt-3 text-xs space-y-1" data-testid="bulk-aifill-result">
              <div className="font-medium">{result.updated_plants} von {result.processed} Pflanzen aktualisiert</div>
              {result.errors > 0 && <div style={{ color: "var(--wp-toxic)" }}>{result.errors} Fehler</div>}
              <details className="opacity-70">
                <summary className="cursor-pointer">Details anzeigen</summary>
                <ul className="mt-1 max-h-32 overflow-y-auto">
                  {result.details.map((d, i) => (
                    <li key={i} className="truncate">
                      <span className={d.status === "ok" ? "text-green-700" : d.status === "error" ? "text-red-700" : "opacity-60"}>●</span>{" "}
                      {d.name}{d.updated?.length > 0 && ` (${d.updated.filter(x => x !== "updated_at").join(", ")})`}
                    </li>
                  ))}
                </ul>
              </details>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AIPlantFillButton({ plant, onFilled }) {
  const [busy, setBusy] = useState(false);
  const fill = async () => {
    if (!plant?.id) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/plants/${plant.id}/ai-fill`);
      if (r.data.updated && r.data.updated.length > 0) {
        toast.success(`${r.data.updated.length} Felder von KI gefüllt: ${r.data.updated.join(", ")}`);
        onFilled(r.data.plant);
      } else {
        toast.info(r.data.note || "Alle Felder bereits ausgefüllt");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "KI-Vervollständigung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={fill}
      disabled={busy}
      className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full shrink-0"
      style={{ backgroundColor: "var(--wp-sand)", color: "var(--wp-forest)", opacity: busy ? 0.6 : 1 }}
      title="Leere Felder mit Claude füllen"
      data-testid="plant-ai-fill"
    >
      <i className="fa-solid fa-wand-magic-sparkles text-xs" /> {busy ? "KI füllt …" : "Lücken mit KI füllen"}
    </button>
  );
}

function AIPlantImageButton({ plant, onGenerated, label = "KI-Bild generieren", small = false }) {
  const [open, setOpen] = useState(false);
  const [part, setPart] = useState("ganze");
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    if (!plant?.name_de) { toast.error("Bitte zuerst Pflanzennamen eingeben"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/plants/ai-image", {
        name_de: plant.name_de,
        name_lat: plant.name_lat || "",
        part,
      });
      onGenerated(r.data.url);
      toast.success("Bild generiert");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler bei der Bild-Generierung");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`${small ? "btn-secondary !py-1.5 !px-3 text-xs" : "btn-secondary !py-2 !px-3 text-sm"} mt-2`}
        data-testid="ai-image-button"
      >
        <i className="fa-solid fa-sparkles text-xs" /> {label}
      </button>
    );
  }

  return (
    <div className="mt-2 p-3 rounded-lg border" style={{ borderColor: "var(--wp-border)", backgroundColor: "var(--wp-cream)" }} data-testid="ai-image-panel">
      <div className="overline mb-2">Pflanzenteil wählen</div>
      <div className="flex flex-wrap gap-2 mb-3">
        {PLANT_PARTS.map((p) => (
          <button
            key={p.v}
            type="button"
            onClick={() => setPart(p.v)}
            className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${part === p.v ? "bg-[var(--wp-primary)] text-[var(--wp-cream)] border-[var(--wp-primary)]" : "bg-white"}`}
            style={part !== p.v ? { borderColor: "var(--wp-border)" } : {}}
            data-testid={`ai-part-${p.v}`}
          >
            {p.l}
          </button>
        ))}
      </div>
      <div className="flex gap-2">
        <button type="button" onClick={generate} disabled={busy} className="btn-primary !py-2 !px-4 text-sm" data-testid="ai-image-generate">
          {busy ? "Generiere…" : "Generieren"}
        </button>
        <button type="button" onClick={() => setOpen(false)} className="btn-secondary !py-2 !px-4 text-sm">Abbrechen</button>
      </div>
      {busy && <div className="text-xs opacity-70 mt-2">Nano Banana erstellt dein Bild – ca. 10-30 Sek…</div>}
    </div>
  );
}

function LookAlikePicker({ selected, onChange, excludeSlug }) {
  const [plants, setPlants] = useState([]);
  const [query, setQuery] = useState("");
  useEffect(() => {
    api.get("/plants").then((r) => setPlants((r.data || []).filter((p) => p.slug !== excludeSlug)));
  }, [excludeSlug]);
  const selectedPlants = plants.filter((p) => selected.includes(p.slug));
  const filtered = query
    ? plants.filter((p) => !selected.includes(p.slug) && (p.name_de.toLowerCase().includes(query.toLowerCase()) || p.name_lat.toLowerCase().includes(query.toLowerCase()))).slice(0, 10)
    : [];
  return (
    <div className="space-y-2" data-testid="lookalike-picker">
      {selectedPlants.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedPlants.map((p) => (
            <span key={p.slug} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs border" style={{ backgroundColor: "var(--wp-cream)", borderColor: "var(--wp-border)" }}>
              {p.name_de}
              <button type="button" onClick={() => onChange(selected.filter((s) => s !== p.slug))} className="text-[var(--wp-toxic)] hover:opacity-70">✕</button>
            </span>
          ))}
        </div>
      )}
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Pflanze suchen und hinzufügen…"
        className="w-full px-3 py-2 rounded-lg border text-sm"
        style={{ borderColor: "var(--wp-border)" }}
        data-testid="lookalike-search"
      />
      {filtered.length > 0 && (
        <div className="max-h-48 overflow-y-auto border rounded-lg" style={{ borderColor: "var(--wp-border)" }}>
          {filtered.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => { onChange([...selected, p.slug]); setQuery(""); }}
              className="w-full flex items-center gap-2 p-2 hover:bg-[var(--wp-cream)] text-left text-sm"
              data-testid={`lookalike-add-${p.slug}`}
            >
              {p.image_url && <img src={p.image_url} alt={p.name_de} className="w-8 h-8 rounded object-cover" />}
              <div className="flex-1">
                <div>{p.name_de}</div>
                <div className="text-xs opacity-60 italic">{p.name_lat}</div>
              </div>
              <PlantBadge category={p.category} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CommunityAdmin() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);

  const load = () => { setLoading(true); api.get("/admin/community/recipes").then((r) => setList(r.data || [])).finally(() => setLoading(false)); };
  useEffect(load, []);

  const del = async (id) => { if (!confirm("Rezept löschen?")) return; await api.delete(`/admin/community/recipes/${id}`); toast.success("Gelöscht"); load(); };
  const delComment = (rid, cid) => { if (!window.confirm("Kommentar löschen?")) return; api.delete(`/admin/community/recipes/${rid}/comments/${cid}`).then(() => { toast.success("Kommentar gelöscht"); load(); }); };

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/community/recipes/${edit.id}`, {
        title: edit.title,
        text: edit.text,
        plants: typeof edit.plants === "string" ? edit.plants.split(",").map((s) => s.trim()).filter(Boolean) : edit.plants,
      });
      toast.success("Gespeichert");
      setEdit(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler");
    }
  };

  if (loading) return <Spinner />;

  return (
    <div className="space-y-4" data-testid="admin-community">
      <div className="text-sm opacity-70 mb-2">{list.length} Community-Rezepte</div>
      {list.length === 0 && <div className="text-sm opacity-70">Keine Rezepte.</div>}
      {list.map((r) => (
        <div key={r.id} className="plant-card p-5" data-testid={`community-recipe-${r.id}`}>
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="flex-1">
              <h4 className="font-display text-xl">{r.title}</h4>
              <div className="text-xs opacity-70">
                von {r.user_name || "Anonym"} · {new Date(r.created_at).toLocaleDateString("de-DE")} · ♥ {(r.likes || []).length} · 💬 {(r.comments || []).length}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => setEdit({ ...r, plants: (r.plants || []).join(",") })} className="text-[var(--wp-primary)]" data-testid={`edit-community-${r.id}`}><Pencil className="w-4 h-4" /></button>
              <button onClick={() => del(r.id)} className="text-[var(--wp-toxic)]" data-testid={`delete-community-${r.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
          {r.plants && r.plants.length > 0 && (
            <div className="text-xs opacity-80 mb-2">🌿 {r.plants.join(", ")}</div>
          )}
          <p className="text-sm whitespace-pre-wrap mb-3">{r.text}</p>
          {(r.comments || []).length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer opacity-70">Kommentare ({r.comments.length})</summary>
              <div className="mt-2 space-y-2">
                {r.comments.map((c) => (
                  <div key={c.id} className="flex items-start justify-between gap-2 p-2 rounded bg-[var(--wp-cream)]">
                    <div className="flex-1">
                      <div className="font-medium text-xs">{c.user_name || "Anonym"} <span className="opacity-50">· {new Date(c.created_at).toLocaleDateString("de-DE")}</span></div>
                      <div>{c.text}</div>
                    </div>
                    <button onClick={() => delComment(r.id, c.id)} className="text-[var(--wp-toxic)] shrink-0" data-testid={`delete-comment-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      ))}

      {edit && (
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ backgroundColor: "rgba(26,47,28,0.6)" }} onClick={() => setEdit(null)}>
          <div className="bg-white rounded-2xl max-w-xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-2xl mb-4">Community-Rezept bearbeiten</h3>
            <form onSubmit={save} className="space-y-3 text-sm">
              <input required placeholder="Titel" value={edit.title || ""} onChange={(e) => setEdit({ ...edit, title: e.target.value })} className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} data-testid="community-form-title" />
              <input placeholder="Pflanzen (kommagetrennt)" value={edit.plants || ""} onChange={(e) => setEdit({ ...edit, plants: e.target.value })} className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} />
              <textarea placeholder="Rezept-Text" value={edit.text || ""} onChange={(e) => setEdit({ ...edit, text: e.target.value })} className="w-full px-3 py-2 rounded-lg border min-h-[140px]" style={{ borderColor: "var(--wp-border)" }} data-testid="community-form-text" />
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setEdit(null)} className="btn-secondary">Abbrechen</button>
                <button className="btn-primary" data-testid="community-form-submit">Speichern</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}




const THEME_PRESETS = {
  default: {
    label: "Default (Sand/Salbei/Oliv)",
    theme_sand: "#CBBCA5", theme_cream: "#E7DED0", theme_sage: "#8C8A6A",
    theme_olive: "#5F5B3E", theme_forest: "#3A392C", theme_text_muted: "#6E6A55",
    theme_toxic: "#A73A3A", theme_medicinal: "#C9A36E", theme_border: "#D5C9B4",
  },
  spring: {
    label: "🌱 Frühling (frisch · zart)",
    theme_sand: "#DDE5C8", theme_cream: "#F1F5E5", theme_sage: "#8FA86C",
    theme_olive: "#4F6B2F", theme_forest: "#2E3B1F", theme_text_muted: "#6B7858",
    theme_toxic: "#C84A3A", theme_medicinal: "#D9B864", theme_border: "#CCD6B6",
  },
  summer: {
    label: "☀️ Sommer (warm · sonnig)",
    theme_sand: "#E8C994", theme_cream: "#FFF3DC", theme_sage: "#A89455",
    theme_olive: "#7A5A2C", theme_forest: "#3A2E1E", theme_text_muted: "#8B7654",
    theme_toxic: "#C8482E", theme_medicinal: "#E0A455", theme_border: "#D9C595",
  },
  autumn: {
    label: "🍂 Herbst (erdig · golden)",
    theme_sand: "#C9A878", theme_cream: "#EBDDC4", theme_sage: "#8B6A3F",
    theme_olive: "#5C3E1F", theme_forest: "#332217", theme_text_muted: "#735840",
    theme_toxic: "#9A3A2E", theme_medicinal: "#C8884A", theme_border: "#C9B083",
  },
  winter: {
    label: "❄️ Winter (kühl · gedämpft)",
    theme_sand: "#C5C2BA", theme_cream: "#E8E6DF", theme_sage: "#7B8079",
    theme_olive: "#4E544D", theme_forest: "#2A2E2A", theme_text_muted: "#646962",
    theme_toxic: "#9C5252", theme_medicinal: "#A89F84", theme_border: "#CFCEC7",
  },
};

const COLOR_KEYS = [
  { key: "theme_sand", label: "Sand (Akzent-Hintergrund)" },
  { key: "theme_cream", label: "Creme (Page-Hintergrund)" },
  { key: "theme_sage", label: "Salbei (Buttons, Icons)" },
  { key: "theme_olive", label: "Oliv (Logo, Headlines)" },
  { key: "theme_forest", label: "Wald (Body-Text)" },
  { key: "theme_text_muted", label: "Text gedämpft" },
  { key: "theme_toxic", label: "Warnung / Giftig (Rot)" },
  { key: "theme_medicinal", label: "Heilpflanzen (Akzent)" },
  { key: "theme_border", label: "Rahmen / Trenner" },
];

const RADIUS_OPTIONS = [
  { value: "0px", label: "Eckig" },
  { value: "8px", label: "Sanft (8px)" },
  { value: "16px", label: "Mittel (16px)" },
  { value: "24px", label: "Stark (24px)" },
  { value: "9999px", label: "Pille (rund)" },
];

const SCALE_OPTIONS = [
  { value: 0.9, label: "Kompakt (–10%)" },
  { value: 1.0, label: "Normal" },
  { value: 1.05, label: "Etwas größer (+5%)" },
  { value: 1.1, label: "Groß (+10%)" },
];

function ThemeEditor({ s, upd }) {
  const applyPreset = (key) => {
    const p = THEME_PRESETS[key];
    if (!p) return;
    if (!window.confirm(`Theme «${p.label}" jetzt auf alle Farb-Felder anwenden? (Du musst danach noch «Speichern" klicken.)`)) return;
    for (const k of Object.keys(p)) {
      if (k === "label") continue;
      upd(k, p[k]);
    }
    upd("theme_active_preset", key);
  };

  return (
    <div>
      <p className="text-xs opacity-60 mb-3">
        Die Werte werden sofort live auf der Seite übernommen (CSS-Variablen). Du siehst die Änderung im Header/Footer/Buttons direkt — speichern macht sie dauerhaft.
      </p>

      <div className="mb-5">
        <label className="overline mb-2 block">Saisonale Presets</label>
        <div className="flex flex-wrap gap-2">
          {Object.entries(THEME_PRESETS).map(([key, p]) => (
            <button
              key={key}
              type="button"
              onClick={() => applyPreset(key)}
              className="px-3 py-2 rounded-full text-xs border"
              style={{
                borderColor: s.theme_active_preset === key ? "var(--wp-olive)" : "var(--wp-border)",
                backgroundColor: s.theme_active_preset === key ? "var(--wp-cream)" : "transparent",
                fontWeight: s.theme_active_preset === key ? 600 : 400,
              }}
              data-testid={`theme-preset-${key}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <p className="text-[11px] opacity-60 mt-1">Preset wendet alle 9 Farben in einem Rutsch an.</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mb-5">
        {COLOR_KEYS.map(({ key, label }) => (
          <div key={key}>
            <label className="overline text-[10px]">{label}</label>
            <div className="flex items-center gap-2 mt-1">
              <input
                type="color"
                value={s[key] || "#000000"}
                onChange={(e) => upd(key, e.target.value)}
                className="w-10 h-9 rounded border cursor-pointer shrink-0"
                data-testid={`theme-color-${key}`}
              />
              <input
                value={s[key] || ""}
                onChange={(e) => upd(key, e.target.value)}
                className="flex-1 px-2 py-1.5 rounded-lg border text-xs font-mono"
                style={{ borderColor: "var(--wp-border)" }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-5">
        <div>
          <label className="overline mb-1 block">Button-Form</label>
          <select
            value={s.theme_button_radius || "9999px"}
            onChange={(e) => upd("theme_button_radius", e.target.value)}
            className="w-full px-3 py-2 rounded-lg border text-sm"
            style={{ borderColor: "var(--wp-border)", backgroundColor: "#fff" }}
            data-testid="theme-button-radius"
          >
            {RADIUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="overline mb-1 block">Schriftgröße global</label>
          <select
            value={String(s.theme_font_scale ?? 1.0)}
            onChange={(e) => upd("theme_font_scale", parseFloat(e.target.value))}
            className="w-full px-3 py-2 rounded-lg border text-sm"
            style={{ borderColor: "var(--wp-border)", backgroundColor: "#fff" }}
            data-testid="theme-font-scale"
          >
            {SCALE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className="overline mb-2 block">Vorschau</label>
        <div className="p-5 rounded-2xl border" style={{ borderColor: "var(--wp-border)", backgroundColor: "var(--wp-cream)" }} data-testid="theme-preview">
          <div className="overline mb-1" style={{ color: "var(--wp-olive)" }}>VORSCHAU · LIVE</div>
          <div className="font-display text-3xl mb-2" style={{ color: "var(--wp-olive)" }}>Wildkräuter sammeln</div>
          <p className="text-sm mb-3" style={{ color: "var(--wp-forest)" }}>Lass dich von der Natur Luxemburgs überraschen.</p>
          <p className="text-xs mb-4" style={{ color: "var(--wp-text-muted)" }}>Mit gedämpftem Text in der zweiten Zeile.</p>
          <div className="flex flex-wrap gap-3 items-center">
            <button type="button" className="btn-primary !py-2 !px-5">Primärer Button</button>
            <button type="button" className="btn-secondary !py-2 !px-5">Sekundärer Button</button>
            <span className="badge badge-edible">essbar</span>
            <span className="badge badge-toxic">giftig</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function SettingsAdmin() {
  const [s, setS] = useState(null);
  const [previewKey, setPreviewKey] = useState(0);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <Spinner />;

  const upd = (k, v) => setS({ ...s, [k]: v });

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/settings", s);
      toast.success("Einstellungen gespeichert");
      // Refresh embed live-preview iframes so they reflect the freshly saved settings
      setPreviewKey((k) => k + 1);
    } catch {
      toast.error("Konnte nicht speichern");
    }
  };

  const txt = (label, key, multi = false, rows = 3) => (
    <div>
      <label className="overline">{label}</label>
      {multi ? (
        <textarea value={s[key] || ""} onChange={(e) => upd(key, e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wp-border)", minHeight: rows * 24 }} data-testid={`setting-${key}`} />
      ) : (
        <input value={s[key] || ""} onChange={(e) => upd(key, e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--wp-border)" }} data-testid={`setting-${key}`} />
      )}
    </div>
  );

  const img = (label, key) => (
    <div>
      <label className="overline">{label}</label>
      <div className="flex items-start gap-3 mt-1">
        <ImageUpload value={s[key] || ""} onChange={(v) => upd(key, v)} endpoint="/admin/uploads" testId={`setting-img-${key}`} />
        <input value={s[key] || ""} onChange={(e) => upd(key, e.target.value)} placeholder="oder URL einfügen" className="flex-1 px-3 py-2 rounded-lg border text-xs" style={{ borderColor: "var(--wp-border)" }} />
      </div>
    </div>
  );

  const card = (title, children) => (
    <div className="plant-card p-5 space-y-3">
      <div className="overline border-b pb-2" style={{ borderColor: "var(--wp-border)" }}>{title}</div>
      {children}
    </div>
  );

  return (
    <form onSubmit={save} className="space-y-5" data-testid="admin-settings">
      <div className="flex items-center justify-between sticky top-0 bg-[var(--wp-cream)] py-3 z-10 -mx-2 px-2 border-b" style={{ borderColor: "var(--wp-border)" }}>
        <div className="text-sm opacity-70">Alle Texte und Bilder der öffentlichen Website</div>
        <button className="btn-primary" data-testid="settings-save"><i className="fa-solid fa-floppy-disk text-sm mr-1.5" /> Speichern</button>
      </div>

      {card("Allgemein", (
        <>
          {txt("App-Name", "app_name")}
          {txt("Kontakt-E-Mail", "contact_email")}
          <div>
            <label className="overline">Primärfarbe</label>
            <div className="flex items-center gap-2 mt-1">
              <input type="color" value={s.primary_color || "#2D5A27"} onChange={(e) => upd("primary_color", e.target.value)} className="w-12 h-10 rounded border cursor-pointer" />
              <input value={s.primary_color || ""} onChange={(e) => upd("primary_color", e.target.value)} className="flex-1 px-3 py-2 rounded-lg border text-sm font-mono" style={{ borderColor: "var(--wp-border)" }} />
            </div>
          </div>
        </>
      ))}

      {card("🎨 Theme & Brand-Farben", (
        <ThemeEditor s={s} upd={upd} />
      ))}

      {card("Header / Navigation", (
        <>
          <div>
            <label className="overline">Logo (Header)</label>
            <div className="flex items-start gap-3 mt-1">
              <ImageUpload value={s.header_logo_url || ""} onChange={(v) => upd("header_logo_url", v)} endpoint="/admin/uploads" testId="setting-img-header_logo_url" />
              <input value={s.header_logo_url || ""} onChange={(e) => upd("header_logo_url", e.target.value)} placeholder="leer = Standard /logos/logo-v3.png" className="flex-1 px-3 py-2 rounded-lg border text-xs" style={{ borderColor: "var(--wp-border)" }} data-testid="setting-header_logo_url" />
            </div>
            {(s.header_logo_url || "") && (
              <div className="mt-2 inline-flex items-center gap-3 p-2 rounded-lg border" style={{ borderColor: "var(--wp-border)", backgroundColor: "#fff" }}>
                <img src={s.header_logo_url} alt="Header-Logo Vorschau" className="h-12 w-auto" />
                <button type="button" onClick={() => upd("header_logo_url", "")} className="text-xs underline opacity-70 hover:opacity-100" data-testid="header-logo-reset">
                  Auf Standard zurücksetzen
                </button>
              </div>
            )}
            <p className="text-xs opacity-60 mt-1">
              Leer lassen, um das verdrahtete Brand-Logo (`/logos/logo-v3.png`) zu verwenden – das ist die sicherste Option für die Produktions-Stabilität.
            </p>
          </div>
          {txt("Login-Button-Text (rechts oben)", "header_login_label")}
        </>
      ))}

      {card("🔧 Wartungsmodus", (
        <>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={!!s.maintenance_mode} onChange={(e) => upd("maintenance_mode", e.target.checked)} className="mt-1" data-testid="setting-maintenance-mode" />
            <div>
              <div className="font-medium">Wartungsmodus aktivieren</div>
              <div className="text-xs opacity-70">Besucher sehen nur die Wartungsseite. Du selbst kannst /admin weiterhin nutzen, um den Modus wieder zu deaktivieren.</div>
            </div>
          </label>
          <div>
            <label className="overline">Logo (Wartungsseite – leer = /logos/logo-v1.png)</label>
            <div className="flex items-start gap-3 mt-1">
              <ImageUpload value={s.maintenance_logo_url || ""} onChange={(v) => upd("maintenance_logo_url", v)} endpoint="/admin/uploads" testId="setting-img-maintenance_logo_url" />
              <input value={s.maintenance_logo_url || ""} onChange={(e) => upd("maintenance_logo_url", e.target.value)} placeholder="leer = Standard" className="flex-1 px-3 py-2 rounded-lg border text-xs" style={{ borderColor: "var(--wp-border)" }} data-testid="setting-maintenance_logo_url" />
            </div>
            {(s.maintenance_logo_url || "") && (
              <button type="button" onClick={() => upd("maintenance_logo_url", "")} className="text-xs underline opacity-70 hover:opacity-100 mt-2" data-testid="maintenance-logo-reset">
                Auf Standard zurücksetzen
              </button>
            )}
          </div>
          {txt("Titel (große Überschrift)", "maintenance_title")}
          {txt("Wartungs-Nachricht (für Besucher)", "maintenance_message", true, 3)}
          {txt("Status-Text (kleine Zeile mit pulsierendem Punkt)", "maintenance_status")}
          {txt("Zitat (kursiv unten, Zeilenumbrüche mit Enter)", "maintenance_quote", true, 3)}

          <div className="mt-3 p-3 rounded-lg" style={{ backgroundColor: "var(--wp-cream)" }}>
            <div className="overline mb-1">🔑 Bypass-Passwort (optional)</div>
            <p className="text-xs opacity-70 mb-2">
              Wenn du hier ein Passwort setzt, können Besucher die Wartungsseite mit einem «Zugang mit Passwort»-Link umgehen und die echte Seite sehen. Praktisch um Kunden/Testern Preview-Zugang zu geben. Leer = keine Umgehung möglich.
            </p>
            {txt("", "maintenance_bypass_password")}
          </div>
        </>
      ))}

      {card("📤 Embed-Codes für externe Websites", (
        <EmbedSnippets />
      ))}

      <TourTextsSettings s={s} upd={upd} />
      <DontDoSettings s={s} upd={upd} />
      <CalendarPhotosSettings s={s} upd={upd} />
      <ContactTextsSettings s={s} upd={upd} />

      {card("🎨 Embed-Widgets – Texte & Farbe", (
        <>
          <p className="text-xs opacity-60 -mt-1 mb-1">
            Diese Werte erscheinen in den Iframes, die du auf externen Websites (WordPress, Wix etc.) einbettest.
          </p>
          <div className="grid lg:grid-cols-[1fr,360px] gap-6 items-start">
            <div className="space-y-3">
              <div>
                <label className="overline">Markenfarbe (Buchen-Button, Empty-Link)</label>
                <div className="flex items-center gap-2 mt-1">
                  <input type="color" value={s.embed_brand_color || "#2D5A27"} onChange={(e) => upd("embed_brand_color", e.target.value)} className="w-12 h-10 rounded border cursor-pointer" data-testid="setting-embed_brand_color-picker" />
                  <input value={s.embed_brand_color || ""} onChange={(e) => upd("embed_brand_color", e.target.value)} className="flex-1 px-3 py-2 rounded-lg border text-sm font-mono" style={{ borderColor: "var(--wp-border)" }} data-testid="setting-embed_brand_color" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {txt("«Powered by ...» Text (mit Link zur Website)", "embed_powered_by")}
                {txt("Buchen-Button-Text", "embed_book_label")}
                {txt("Ausgebucht-Label", "embed_sold_out_label")}
                {txt("Warteliste-Label (wenn Tour voll)", "embed_waitlist_label")}
                {txt("Gratis-Badge-Text", "embed_free_label")}
                {txt("Lade-Text", "embed_loading_label")}
                {txt("Empty-State CTA-Label", "embed_empty_cta_label")}
              </div>
              {txt("Empty-State Titel (wenn keine Touren geplant)", "embed_empty_title")}
              {txt("Empty-State Beschreibung", "embed_empty_text", true, 3)}
            </div>
            <div className="lg:sticky lg:top-24">
              <div className="overline mb-2 flex items-center justify-between">
                <span>Live-Vorschau (Touren)</span>
                <button
                  type="button"
                  onClick={() => setPreviewKey((k) => k + 1)}
                  className="text-[10px] underline opacity-70 hover:opacity-100 normal-case font-normal"
                  data-testid="embed-preview-refresh"
                >
                  Neu laden
                </button>
              </div>
              <div className="rounded-xl overflow-hidden border bg-white" style={{ borderColor: "var(--wp-border)" }}>
                <iframe
                  key={`tours-${previewKey}`}
                  src={`/embed/tours?limit=3&preview=${previewKey}`}
                  title="Embed-Vorschau Touren"
                  className="w-full"
                  style={{ height: 420, border: 0, display: "block" }}
                  data-testid="embed-preview-tours"
                />
              </div>
              <div className="overline mt-4 mb-2">Live-Vorschau (Pflanzen)</div>
              <div className="rounded-xl overflow-hidden border bg-white" style={{ borderColor: "var(--wp-border)" }}>
                <iframe
                  key={`plants-${previewKey}`}
                  src={`/embed/plants?preview=${previewKey}`}
                  title="Embed-Vorschau Pflanzen"
                  className="w-full"
                  style={{ height: 360, border: 0, display: "block" }}
                  data-testid="embed-preview-plants"
                />
              </div>
              <p className="text-[11px] opacity-60 mt-2 leading-relaxed">
                Vorschau zeigt die zuletzt <em>gespeicherten</em> Werte. Klicke oben {`«Speichern"`}, dann aktualisiert sich die Vorschau automatisch.
              </p>
            </div>
          </div>
        </>
      ))}

      {card("Hero-Sektion (Startseite oben)", (
        <>
          {img("Hintergrundbild", "hero_image_url")}
          {txt("Overline (kleiner Text oben)", "hero_overline")}
          {txt("Titel (Zeilenumbrüche mit Enter)", "hero_title", true, 2)}
          {txt("Untertitel", "hero_subtitle", true, 3)}
          <div className="grid sm:grid-cols-2 gap-3">
            {txt("Primärer Button-Text", "hero_cta_primary")}
            {txt("Sekundärer Button-Text", "hero_cta_secondary")}
          </div>
        </>
      ))}

      {card("Quote-Banner (zwischen Hero und Inhalt)", (
        txt("Zitat (Zeilenumbrüche mit Enter)", "quote_text", true, 3)
      ))}

      {card("Home – Brand-Tagline (\u201eNatur erleben. Wissen teilen. Bewusst unterwegs.\")", (
        <div className="grid sm:grid-cols-3 gap-3">
          {txt("Zeile 1", "home_tagline_line1")}
          {txt("Zeile 2", "home_tagline_line2")}
          {txt("Zeile 3", "home_tagline_line3")}
        </div>
      ))}

      {card("Home – Feature-Kacheln (4 Quick-Links unter Hero)", (
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="overline" style={{ color: "var(--wp-olive)" }}>Kachel 1 → /plants</div>
            {txt("Titel", "home_tile1_label")}
            {txt("Untertitel", "home_tile1_sub")}
          </div>
          <div className="space-y-2">
            <div className="overline" style={{ color: "var(--wp-olive)" }}>Kachel 2 → /identify</div>
            {txt("Titel", "home_tile2_label")}
            {txt("Untertitel", "home_tile2_sub")}
          </div>
          <div className="space-y-2">
            <div className="overline" style={{ color: "var(--wp-olive)" }}>Kachel 3 → /tours</div>
            {txt("Titel", "home_tile3_label")}
            {txt("Untertitel", "home_tile3_sub")}
          </div>
          <div className="space-y-2">
            <div className="overline" style={{ color: "var(--wp-olive)" }}>Kachel 4 → /calendar</div>
            {txt("Titel", "home_tile4_label")}
            {txt("Untertitel", "home_tile4_sub")}
          </div>
        </div>
      ))}

      {card("Pflanzen-Erkennung Sektion", (
        <>
          {img("Bild rechts", "recognize_image_url")}
          {txt("Titel", "recognize_title", true, 2)}
          {txt("Beschreibung", "recognize_text", true, 4)}
        </>
      ))}

      {card("Touren-Sektion", (
        <>
          {img("Bild links", "tours_image_url")}
          {txt("Titel", "tours_title", true, 2)}
          {txt("Beschreibung", "tours_text", true, 4)}
        </>
      ))}

      {card("Sicherheits-Hinweis (Box am Ende der Startseite)", (
        <>
          {txt("Titel", "safety_title")}
          {txt("Text", "safety_text", true, 3)}
        </>
      ))}

      {card("Newsletter-Sektion (auf Home)", (
        <>
          {txt("Titel", "newsletter_title")}
          {txt("Text", "newsletter_text", true, 3)}
        </>
      ))}

      {card("Absender „Pflanze der Woche“-E-Mail", (
        <>
          {txt("Absender-Name", "potw_sender_name")}
          {txt("Absender-Adresse", "potw_sender_email")}
          {txt("Antwort-an (Reply-To)", "potw_reply_to")}
        </>
      ))}

      {card("🍪 Cookie-Banner (GDPR)", (
        <>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={!!s.cookie_banner_enabled} onChange={(e) => upd("cookie_banner_enabled", e.target.checked)} className="mt-1" data-testid="setting-cookie_banner_enabled" />
            <div>
              <div className="font-medium">Cookie-Banner anzeigen</div>
              <div className="text-xs opacity-70">Erscheint unten für neue Besucher, bis sie auf {`«Verstanden"`} klicken (gespeichert via localStorage).</div>
            </div>
          </label>
          {txt("Banner-Text", "cookie_banner_text", true, 3)}
          <div className="grid sm:grid-cols-2 gap-3">
            {txt("Accept-Button-Text", "cookie_banner_accept_label")}
            {txt("Link-Label (zur Datenschutz-Seite)", "cookie_banner_privacy_link_label")}
          </div>
          {txt("Link-URL (z. B. /impressum)", "cookie_banner_privacy_link_url")}
        </>
      ))}

      {card("💾 Backup / Daten-Export", (
        <BackupExportButton />
      ))}

      {card("Footer", (
        <>
          <div>
            <label className="overline">Logo (Footer, dunkler Hintergrund – weißes Logo empfohlen)</label>
            <div className="flex items-start gap-3 mt-1">
              <ImageUpload value={s.footer_logo_url || ""} onChange={(v) => upd("footer_logo_url", v)} endpoint="/admin/uploads" testId="setting-img-footer_logo_url" />
              <input value={s.footer_logo_url || ""} onChange={(e) => upd("footer_logo_url", e.target.value)} placeholder="leer = Standard /logos/logo-v3-white.png" className="flex-1 px-3 py-2 rounded-lg border text-xs" style={{ borderColor: "var(--wp-border)" }} data-testid="setting-footer_logo_url" />
            </div>
            {(s.footer_logo_url || "") && (
              <div className="mt-2 inline-flex items-center gap-3 p-2 rounded-lg border" style={{ borderColor: "var(--wp-border)", backgroundColor: "#1A2F1C" }}>
                <img src={s.footer_logo_url} alt="Footer-Logo Vorschau" className="h-12 w-auto" />
                <button type="button" onClick={() => upd("footer_logo_url", "")} className="text-xs underline" style={{ color: "var(--wp-cream)" }} data-testid="footer-logo-reset">
                  Auf Standard zurücksetzen
                </button>
              </div>
            )}
            <p className="text-xs opacity-60 mt-1">
              Empfehlung: Weiße Logo-Variante auf transparentem Hintergrund (PNG) – wegen des dunkelgrünen Footer-Untergrunds.
            </p>
          </div>
          {txt("Tagline (kurzer Spruch unter Logo)", "footer_tagline", true, 3)}
          {txt("Zitat (kursive Zeile unter Logo, Zeilenumbrüche mit Enter)", "footer_quote", true, 3)}
          {txt("Copyright (© und Jahr werden automatisch vorangestellt)", "footer_copyright")}
          {txt("Motto (rechts unten im Footer)", "footer_motto")}
          {txt("Standort (Kontakt-Spalte)", "footer_location")}
          <div className="grid sm:grid-cols-3 gap-3">
            {txt("Instagram-URL", "footer_social_instagram")}
            {txt("Facebook-URL", "footer_social_facebook")}
            {txt("YouTube-URL", "footer_social_youtube")}
          </div>
          <p className="text-xs opacity-60">Leere URL = Icon wird ausgeblendet.</p>
        </>
      ))}
    </form>
  );
}

function BackupExportButton() {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/backup", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      const now = new Date();
      const ts = now.toISOString().slice(0, 10) + "-" + now.toTimeString().slice(0, 8).replace(/:/g, "");
      a.href = url;
      a.download = `wildkraeuter-backup-${ts}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup heruntergeladen");
    } catch {
      toast.error("Backup fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <p className="text-xs opacity-70 mb-3">
        Lädt eine ZIP-Datei mit allen Inhalten herunter (Pflanzen, Touren, Artikel, Settings, Email-Templates, Quiz, Community-Rezepte, Buchungen, Nachrichten, Newsletter-Abos, User ohne Passwörter, Tagebuch-Einträge). Praktisch als Sicherung vor größeren Änderungen.
      </p>
      <button
        type="button"
        onClick={download}
        disabled={busy}
        className="btn-primary !py-2 !px-4"
        data-testid="backup-export-btn"
      >
        <Download className="w-4 h-4" /> {busy ? "Erstelle ZIP…" : "Komplettes Backup herunterladen"}
      </button>
    </div>
  );
}

function CSVControls({ onImportDone }) {
  const [busy, setBusy] = useState(null);
  const [report, setReport] = useState(null);

  const downloadWithPin = async (endpoint, filename) => {
    setBusy(endpoint);
    try {
      const r = await api.get(endpoint, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download fehlgeschlagen");
    } finally {
      setBusy(null);
    }
  };

  const handleImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy("import");
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/admin/plants/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReport(r.data);
      const total = (r.data.created || 0) + (r.data.updated || 0);
      toast.success(`${total} Pflanze${total === 1 ? "" : "n"} importiert (${r.data.created} neu, ${r.data.updated} aktualisiert)`);
      onImportDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import fehlgeschlagen");
    } finally {
      setBusy(null);
      e.target.value = "";
    }
  };

  return (
    <>
      <button onClick={() => downloadWithPin("/admin/plants/csv-template", "wildpflanzen-vorlage.csv")} disabled={busy === "template"} className="btn-secondary !py-2 !px-3" title="CSV-Vorlage herunterladen" data-testid="csv-template-btn">
        <FileSpreadsheet className="w-4 h-4" /> Vorlage
      </button>
      <button onClick={() => downloadWithPin("/admin/plants/export-csv", "wildpflanzen-export.csv")} disabled={busy === "export"} className="btn-secondary !py-2 !px-3" title="Alle Pflanzen exportieren" data-testid="csv-export-btn">
        <Download className="w-4 h-4" /> Export
      </button>
      <label className="btn-secondary !py-2 !px-3 cursor-pointer" title="CSV importieren" data-testid="csv-import-btn">
        <Upload className="w-4 h-4" /> {busy === "import" ? "Importiere…" : "Import"}
        <input type="file" accept=".csv,text/csv" onChange={handleImport} className="hidden" disabled={busy === "import"} data-testid="csv-import-input" />
      </label>

      {report && (
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ backgroundColor: "rgba(26,47,28,0.6)" }} onClick={() => setReport(null)} data-testid="csv-report-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-2xl mb-4">CSV-Import abgeschlossen</h3>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="plant-card p-4 text-center"><div className="overline mb-1">Neu</div><div className="font-display text-2xl text-[var(--wp-primary)]">{report.created}</div></div>
              <div className="plant-card p-4 text-center"><div className="overline mb-1">Aktualisiert</div><div className="font-display text-2xl">{report.updated}</div></div>
              <div className="plant-card p-4 text-center"><div className="overline mb-1">Fehler</div><div className="font-display text-2xl" style={{ color: report.error_count ? "var(--wp-toxic)" : "inherit" }}>{report.error_count}</div></div>
            </div>
            <div className="text-xs opacity-70 mb-3">
              Spalten: <code className="text-[10px]">slug, name_de, name_lat, name_lu, category, tags, image_url, short_description, description, ingredients, season, habitat, look_alikes, uses, warnings, historical_background</code><br/>
              Listen (tags, season) trennt man mit <code>|</code> · Kategorie: <code>edible</code> / <code>medicinal</code> / <code>toxic</code> · Saison: <code>1-12</code>.
            </div>
            {report.errors && report.errors.length > 0 && (
              <div>
                <div className="overline mb-2">Fehler-Details (max. 50)</div>
                <ul className="text-xs space-y-2 max-h-60 overflow-y-auto">
                  {report.errors.map((e, i) => (
                    <li key={i} className="p-2 rounded bg-[var(--wp-cream)]">
                      <b>Zeile {e.row}:</b> {e.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <button onClick={() => setReport(null)} className="btn-primary mt-5 w-full justify-center">Schließen</button>
          </div>
        </div>
      )}
    </>
  );
}


function AnalyticsAdmin() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/analytics?days=${days}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [days]);

  if (loading && !data) return <Spinner />;
  if (!data) return <div className="text-sm opacity-70">Keine Daten</div>;

  const maxDay = Math.max(1, ...data.by_day.map((d) => d.views));
  const maxPath = Math.max(1, ...data.top_paths.map((p) => p.views));

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm opacity-70">{data.total_views} Page Views in den letzten {data.days} Tagen</div>
        <div className="flex gap-1">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} className={`px-3 py-1.5 rounded-full text-xs border ${days === d ? "bg-[var(--wp-primary)] text-[var(--wp-cream)] border-[var(--wp-primary)]" : "bg-white"}`} style={days !== d ? { borderColor: "var(--wp-border)" } : {}} data-testid={`analytics-days-${d}`}>
              {d}T
            </button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="plant-card p-4"><div className="overline opacity-70">Page Views</div><div className="font-display text-3xl">{data.total_views}</div></div>
        <div className="plant-card p-4"><div className="overline opacity-70">Touren-Seite</div><div className="font-display text-3xl">{data.funnel.tours_views}</div><div className="text-xs opacity-60 mt-1">Views</div></div>
        <div className="plant-card p-4"><div className="overline opacity-70">Buchungen</div><div className="font-display text-3xl">{data.funnel.bookings}</div></div>
        <div className="plant-card p-4"><div className="overline opacity-70">Conversion</div><div className="font-display text-3xl">{data.funnel.conversion_pct}%</div><div className="text-xs opacity-60 mt-1">Tours → Buchung</div></div>
      </div>

      <div className="plant-card p-5">
        <div className="overline mb-3">Page Views pro Tag</div>
        {data.by_day.length === 0 ? <div className="text-sm opacity-60">Noch keine Daten in diesem Zeitraum.</div> : (
          <div className="flex items-end gap-1 h-40" data-testid="analytics-chart">
            {data.by_day.map((d, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${d.views} Views`}>
                <div className="text-[10px] opacity-60">{d.views || ""}</div>
                <div className="w-full rounded-t" style={{ height: `${(d.views / maxDay) * 100}%`, backgroundColor: "var(--wp-olive)", minHeight: d.views ? 4 : 1 }} />
                <div className="text-[9px] opacity-50">{d.date.slice(8)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div className="plant-card p-5">
          <div className="overline mb-3">Top Pfade</div>
          {data.top_paths.length === 0 ? <div className="text-sm opacity-60">Noch keine Aufrufe.</div> : (
            <ul className="space-y-2 text-sm">
              {data.top_paths.map((p, i) => (
                <li key={i} className="flex items-center gap-3" data-testid={`analytics-path-${i}`}>
                  <span className="font-mono text-xs truncate flex-1">{p.path}</span>
                  <div className="w-24 h-2 rounded-full overflow-hidden" style={{ backgroundColor: "var(--wp-cream)" }}>
                    <div className="h-full" style={{ width: `${(p.views / maxPath) * 100}%`, backgroundColor: "var(--wp-olive)" }} />
                  </div>
                  <span className="w-8 text-right text-xs opacity-70">{p.views}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="plant-card p-5">
          <div className="overline mb-3">Top Pflanzen</div>
          {data.top_plants.length === 0 ? <div className="text-sm opacity-60">Noch keine Pflanzen-Aufrufe.</div> : (
            <ul className="space-y-2">
              {data.top_plants.map((p, i) => (
                <li key={p.slug} className="flex items-center gap-3" data-testid={`analytics-plant-${i}`}>
                  {p.image_url ? <img src={p.image_url} alt={p.name_de} className="w-9 h-9 rounded-lg object-cover" /> : <div className="w-9 h-9 rounded-lg bg-[var(--wp-cream)]" />}
                  <span className="text-sm flex-1 truncate">{p.name_de}</span>
                  <span className="text-xs opacity-70">{p.views} Views</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <p className="text-xs opacity-60">
        First-party Tracking: nur Pfad + Zeitstempel werden gespeichert. Keine IPs, keine Cookies, keine Third-Party-Tracker.
      </p>
    </div>
  );
}

function AISuggestionsWidget({ onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async (force = false) => {
    setLoading(true);
    setError("");
    try {
      const r = await api.post("/admin/dashboard/ai-suggestions", { force });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "KI-Vorschläge konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(false); }, []);

  const actionTab = { article: "articles", plant: "plants", tour: "tours", image: "plants", info: null };

  return (
    <div className="plant-card p-5" data-testid="dashboard-ai-suggestions">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="overline flex items-center gap-2">
            <i className="fa-solid fa-wand-magic-sparkles" style={{ color: "var(--wp-olive)" }} />
            KI-Vorschläge für Inhalte
          </div>
          <div className="text-xs opacity-60 mt-1">Claude Sonnet 4.5 analysiert deine Datenbank und schlägt nächste Schritte vor.</div>
          {data?.cached && (
            <div className="text-[11px] mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ backgroundColor: "rgba(90,110,63,0.12)", color: "var(--wp-olive)" }} data-testid="ai-suggestions-cached">
              <i className="fa-solid fa-bolt text-[10px]" /> Aus Cache · {data.age_hours}h alt
            </div>
          )}
        </div>
        <button onClick={() => load(true)} disabled={loading} className="btn-secondary !py-1.5 !px-3 text-xs" data-testid="ai-suggestions-refresh" title="Neu analysieren (Claude)">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> {loading ? "Analysiere…" : "Neu laden"}
        </button>
      </div>

      {error && <div className="text-sm" style={{ color: "var(--wp-toxic)" }}>{error}</div>}

      {data?.stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4 text-xs">
          <div className="p-2 rounded-lg" style={{ backgroundColor: "var(--wp-cream)" }}><div className="opacity-60">Pflanzen</div><div className="font-display text-lg">{data.stats.plants_total}</div></div>
          <div className="p-2 rounded-lg" style={{ backgroundColor: "var(--wp-cream)" }}><div className="opacity-60">Ohne Bild</div><div className="font-display text-lg">{data.stats.plants_without_image}</div></div>
          <div className="p-2 rounded-lg" style={{ backgroundColor: "var(--wp-cream)" }}><div className="opacity-60">Ohne Artikel</div><div className="font-display text-lg">{data.stats.plants_without_article}</div></div>
          <div className="p-2 rounded-lg" style={{ backgroundColor: "var(--wp-cream)" }}><div className="opacity-60">Artikel</div><div className="font-display text-lg">{data.stats.articles_total}</div></div>
          <div className="p-2 rounded-lg" style={{ backgroundColor: "var(--wp-cream)" }}><div className="opacity-60">Touren</div><div className="font-display text-lg">{data.stats.tours_total}</div></div>
        </div>
      )}

      {loading && !data && <div className="text-sm opacity-60 py-6 text-center">Claude analysiert deine Inhalte…</div>}

      {data?.suggestions && data.suggestions.length > 0 && (
        <ul className="space-y-2">
          {data.suggestions.map((sug, i) => {
            const tab = actionTab[sug.action];
            return (
              <li key={i} className="flex items-start gap-3 p-3 rounded-lg border" style={{ borderColor: "var(--wp-border)", backgroundColor: "#fff" }} data-testid={`ai-suggestion-${i}`}>
                <div className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center" style={{ backgroundColor: "var(--wp-cream)" }}>
                  <i className={`fa-solid ${sug.icon || "fa-lightbulb"} text-xs`} style={{ color: "var(--wp-olive)" }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{sug.title}</div>
                  <div className="text-xs opacity-70 mt-0.5 leading-relaxed">{sug.text}</div>
                  {sug.action_payload && <div className="text-[11px] opacity-60 mt-1 italic">→ {sug.action_payload}</div>}
                </div>
                {tab && (
                  <button onClick={() => onNavigate(tab)} className="btn-secondary !py-1 !px-3 text-xs shrink-0" data-testid={`ai-suggestion-${i}-cta`}>
                    Öffnen
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {data?.fallback && <div className="text-[11px] opacity-60 mt-3 italic">Fallback-Vorschläge (KI gerade nicht erreichbar)</div>}
    </div>
  );
}

function DashboardAdmin({ onNavigate }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;
  if (!stats) return <div className="text-sm opacity-70">Statistiken konnten nicht geladen werden.</div>;

  const cards = [
    { label: "Pflanzen", value: stats.plants.total, sub: Object.entries(stats.plants.by_category).map(([k, v]) => `${v} ${k}`).join(" · "), tab: "plants", icon: "fa-leaf" },
    { label: "Touren (kommend)", value: stats.tours.upcoming, sub: `${stats.tours.bookings_week} Buchungen / Woche`, tab: "tours", icon: "fa-route" },
    { label: "Buchungen gesamt", value: stats.tours.bookings_total, sub: `${stats.tours.bookings_week} neu diese Woche`, tab: "bookings", icon: "fa-calendar-check" },
    { label: "User", value: stats.users.total, sub: `${stats.users.new_week} neu diese Woche`, tab: "users", icon: "fa-users" },
    { label: "Nachrichten", value: stats.contacts.total, sub: `${stats.contacts.week} neu diese Woche`, tab: "contacts", icon: "fa-envelope" },
    { label: "Community-Rezepte", value: stats.community.total, sub: `${stats.community.week} neu diese Woche`, tab: "community", icon: "fa-comments" },
    { label: "Newsletter", value: stats.newsletter.active, sub: `von ${stats.newsletter.total} Gesamt`, tab: "newsletter", icon: "fa-paper-plane" },
    { label: "Tagebuch-Einträge", value: stats.journal_entries, sub: "von Usern angelegt", tab: null, icon: "fa-book" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <button
            key={c.label}
            onClick={() => c.tab && onNavigate(c.tab)}
            className={`plant-card p-5 text-left ${c.tab ? "hover:shadow-lg transition-shadow cursor-pointer" : "cursor-default"}`}
            data-testid={`dashboard-card-${c.label.toLowerCase().split(" ")[0]}`}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="overline opacity-70">{c.label}</div>
              <i className={`fa-solid ${c.icon} opacity-30`} />
            </div>
            <div className="font-display text-4xl">{c.value}</div>
            <div className="text-xs opacity-60 mt-1">{c.sub}</div>
          </button>
        ))}
      </div>

      {stats.potw && (
        <div className="plant-card p-5 flex items-center gap-4" data-testid="dashboard-potw">
          <img src={stats.potw.image_url} alt={stats.potw.name_de} className="w-20 h-20 rounded-xl object-cover" />
          <div className="flex-1">
            <div className="overline mb-1">Pflanze der Woche</div>
            <div className="font-display text-2xl">{stats.potw.name_de}</div>
            <div className="text-xs opacity-60">gesetzt am {stats.potw.set_at ? new Date(stats.potw.set_at).toLocaleDateString("de-DE") : "—"}</div>
          </div>
          <button onClick={() => onNavigate("plants")} className="btn-secondary !py-2 !px-4 text-sm">Ändern</button>
        </div>
      )}

      <AISuggestionsWidget onNavigate={onNavigate} />

      <div className="plant-card p-5" data-testid="dashboard-activity">
        <div className="overline mb-3">Letzte Aktivitäten</div>
        {stats.activity.length === 0 ? (
          <div className="text-sm opacity-60">Keine Aktivitäten.</div>
        ) : (
          <ul className="space-y-2">
            {stats.activity.map((a, i) => {
              const icons = { booking: "fa-calendar-check", contact: "fa-envelope", community: "fa-comments", signup: "fa-user-plus" };
              return (
                <li key={i} className="flex items-center gap-3 text-sm py-2 border-b last:border-0" style={{ borderColor: "var(--wp-border)" }}>
                  <i className={`fa-solid ${icons[a.type] || "fa-circle"} opacity-50 w-5`} />
                  <span className="flex-1">{a.title}</span>
                  <span className="text-xs opacity-50 shrink-0">{a.at ? new Date(a.at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
